import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  BusinessTrip,
  BonusExtraEntry,
  DEFAULT_METROLOGIST_ROW_LABELS,
  Employee,
  EmployeeRole,
  ExpenseEntry,
  GsmEntry,
  HOUSEHOLD_ITEM_LABELS,
  HouseholdItemType,
  HouseholdMovementDirection,
  HouseholdMovementOperation,
  HouseholdMovementScope,
  HouseholdStockBalances,
  HouseholdStockMovement,
  METROLOGIST_GAS_ROW_KEYS,
  METROLOGIST_HEAT_ROW_KEYS,
  METROLOGIST_WATER_ROW_KEYS,
  MetrologistHeatSummaryBlock,
  MetrologistWaterSummaryBlock,
  MetrologistWork,
  PlanEntry,
  REPORT_ENTRY_ACTION_LABELS,
  REPORT_ENTRY_ENTITY_LABELS,
  ReportEntryAuditLog,
  ReportEntryEntityType,
  RevenueHandoverEntry,
  ServiceCostEntry,
  reportApi,
  SalaryService,
  SalaryServiceVariantPayload,
} from '../services/reportApi';
import {
  BanknotesIcon,
  BeakerIcon,
  CalendarDaysIcon,
  ClipboardDocumentListIcon,
  CommandLineIcon,
  CubeIcon,
  GiftIcon,
  MapPinIcon,
  PencilIcon,
  ReceiptPercentIcon,
  TrashIcon,
  UserGroupIcon,
  WrenchScrewdriverIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { Dropdown } from 'primereact/dropdown';
import { MultiSelect } from 'primereact/multiselect';
import { CITY_OPTIONS } from '../utils/cityUtils';
import { useAuth } from '../contexts/AuthContext';

type ReportTab =
  | 'metrologists'
  | 'employees'
  | 'products'
  | 'bonusExtras'
  | 'gsmDev'
  | 'revenueHandovers'
  | 'expensesDev'
  | 'plansDev'
  | 'businessTripsDev'
  | 'serviceCostsDev'
  | 'householdDev'
  | 'auditConsole';

type VariantFormItem = {
  id?: string;
  name: string;
  rateType: 'fixed' | 'percentage';
  fixedPrice: string;
  percentage: string;
  bitrixProductId: string;
  isActive: boolean;
  monthRates: VariantMonthRateFormItem[];
};

type VariantMonthRateFormItem = {
  year: string;
  month: string;
  rateType: 'fixed' | 'percentage';
  fixedPrice: string;
  percentage: string;
  isActive: boolean;
};

type ServiceFormState = {
  name: string;
  description: string;
  isActive: boolean;
  variants: VariantFormItem[];
};

type EmployeeFormState = {
  fullName: string;
  city: string;
  role: EmployeeRole;
  bitrixUserId: string;
  isActive: boolean;
  inactiveFromMonth: string;
  inactiveFromYear: string;
};

type DevelopmentEntryFormState = {
  city: string;
  employeeId: string;
  amount: string;
  date: string;
  comment: string;
};

type ExpenseFormState = {
  city: string;
  employeeId: string;
  paymentType: 'cash' | 'bank';
  amount: string;
  date: string;
  comment: string;
};

type PlanFormState = {
  city: string;
  month: string;
  amount: string;
};

type BusinessTripFormState = {
  city: string;
  employeeId: string;
  from: string;
  to: string;
  comment: string;
};

type HouseholdFormState = {
  city: string;
  employeeId: string;
  itemType: HouseholdItemType;
  scope: HouseholdMovementScope;
  direction: HouseholdMovementDirection;
  operationType: HouseholdMovementOperation;
  quantity: string;
  date: string;
  dealId: string;
  comment: string;
};

type ServiceCostFormState = {
  cities: string[];
  month: string;
  waterVerification: string;
  waterReplacement: string;
  waterSale: string;
  waterSealing: string;
  waterDiagnostics: string;
  heatVerification: string;
  heatMounting: string;
  heatSale: string;
  heatBatteryReplacement: string;
  gasVerification: string;
  bankReceiptTaxPercent: string;
  perRequestExpense: string;
  dispatcherExpense: string;
};

type ServiceCostFieldKey = Exclude<keyof ServiceCostFormState, 'cities' | 'month'>;
type ServiceCostExpenseFieldKey = 'bankReceiptTaxPercent' | 'perRequestExpense' | 'dispatcherExpense';

const initialForm: ServiceFormState = {
  name: '',
  description: '',
  isActive: true,
  variants: [],
};

const initialEmployeeForm: EmployeeFormState = {
  fullName: '',
  city: '',
  role: 'METROLOGIST',
  bitrixUserId: '',
  isActive: true,
  inactiveFromMonth: '',
  inactiveFromYear: '',
};

const initialDevelopmentEntryForm: DevelopmentEntryFormState = {
  city: '',
  employeeId: '',
  amount: '',
  date: '',
  comment: '',
};

const initialExpenseForm: ExpenseFormState = {
  city: '',
  employeeId: '',
  paymentType: 'cash',
  amount: '',
  date: '',
  comment: '',
};

const initialPlanForm: PlanFormState = {
  city: '',
  month: `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, '0')}`,
  amount: '',
};

const initialBusinessTripForm: BusinessTripFormState = {
  city: '',
  employeeId: '',
  from: '',
  to: '',
  comment: '',
};

const initialHouseholdForm: HouseholdFormState = {
  city: '',
  employeeId: '',
  itemType: 'water_counter',
  scope: 'warehouse',
  direction: 'in',
  operationType: 'receipt',
  quantity: '',
  date: '',
  dealId: '',
  comment: '',
};

const HOUSEHOLD_ITEM_OPTIONS = (Object.keys(HOUSEHOLD_ITEM_LABELS) as HouseholdItemType[]).map((value) => ({
  label: HOUSEHOLD_ITEM_LABELS[value],
  value,
}));

const HOUSEHOLD_OPERATION_OPTIONS: Array<{ label: string; value: HouseholdMovementOperation }> = [
  { label: 'Приход на склад', value: 'receipt' },
  { label: 'Выдача метрологу', value: 'issue' },
  { label: 'Возврат на склад', value: 'return' },
  { label: 'Списание по сотруднику', value: 'consume' },
];

const HOUSEHOLD_OPERATION_LABELS: Record<HouseholdMovementOperation, string> = {
  opening: 'Начальный остаток',
  receipt: 'Приход на склад',
  issue: 'Выдача сотруднику',
  return: 'Возврат от сотрудника',
  consume: 'Списание у сотрудника',
  adjustment: 'Корректировка',
};

const initialServiceCostForm: ServiceCostFormState = {
  cities: [],
  month: `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, '0')}`,
  waterVerification: '',
  waterReplacement: '',
  waterSale: '',
  waterSealing: '',
  waterDiagnostics: '',
  heatVerification: '',
  heatMounting: '',
  heatSale: '',
  heatBatteryReplacement: '',
  gasVerification: '',
  bankReceiptTaxPercent: '',
  perRequestExpense: '',
  dispatcherExpense: '',
};

const SERVICE_COST_FIELDS: Array<{ label: string; key: ServiceCostFieldKey }> = [
  { label: 'ВС поверка', key: 'waterVerification' },
  { label: 'ВС замена', key: 'waterReplacement' },
  { label: 'ВС продажа', key: 'waterSale' },
  { label: 'ВС опломбировка', key: 'waterSealing' },
  { label: 'ВС диагностика', key: 'waterDiagnostics' },
  { label: 'ТС поверка', key: 'heatVerification' },
  { label: 'ТС монтаж/демонтаж', key: 'heatMounting' },
  { label: 'ТС продажа', key: 'heatSale' },
  { label: 'ТС замена АКБ', key: 'heatBatteryReplacement' },
  { label: 'ГС поверка', key: 'gasVerification' },
];

const SERVICE_COST_EXPENSE_FIELDS: Array<{ label: string; key: ServiceCostExpenseFieldKey; suffix?: string }> = [
  { label: 'Налоги с поступления р/сч', key: 'bankReceiptTaxPercent', suffix: '%' },
  { label: 'Расходы на 1 заявку', key: 'perRequestExpense' },
  { label: 'Диспетчер', key: 'dispatcherExpense' },
];

const getMonthStartDate = (): string => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;
};

const getTodayDate = (): string => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(
    now.getDate(),
  ).padStart(2, '0')}`;
};

const formatDateRu = (value?: string | null): string => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
};

const formatMonthYearRu = (value?: string | null): string => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('ru-RU', {
    month: '2-digit',
    year: 'numeric',
  });
};

const AUDIT_FIELD_LABELS: Record<string, string> = {
  amount: 'Сумма',
  date: 'Дата',
  comment: 'Комментарий',
  employeeName: 'Сотрудник',
  paymentType: 'Тип оплаты',
  year: 'Год',
  month: 'Месяц',
};

const AUDIT_ACTOR_TYPE_LABELS: Record<string, string> = {
  admin: 'Админ',
  salary: 'Зарплата',
};

const formatAuditValue = (key: string, value: unknown): string => {
  if (value == null || value === '') return '—';
  if (key === 'amount' && typeof value === 'number') return `${value.toLocaleString('ru-RU')} ₽`;
  if (key === 'date') return formatDateRu(String(value));
  if (key === 'paymentType') return value === 'bank' ? 'Безнал' : 'Нал';
  if (key === 'month' && typeof value === 'number') {
    const months = ['', 'Янв', 'Фев', 'Мар', 'Апр', 'Май', 'Июн', 'Июл', 'Авг', 'Сен', 'Окт', 'Ноя', 'Дек'];
    return months[value] || String(value);
  }
  return String(value);
};

const describeAuditSnapshot = (snapshot?: Record<string, unknown> | null): string => {
  if (!snapshot) return '—';
  const keys = ['date', 'year', 'month', 'employeeName', 'amount', 'paymentType', 'comment'];
  const parts: string[] = [];
  for (const key of keys) {
    if (snapshot[key] !== undefined) {
      parts.push(`${AUDIT_FIELD_LABELS[key] || key}: ${formatAuditValue(key, snapshot[key])}`);
    }
  }
  return parts.length ? parts.join('; ') : '—';
};

const describeAuditChange = (log: ReportEntryAuditLog): string => {
  if (log.action === 'CREATE') return describeAuditSnapshot(log.after);
  if (log.action === 'DELETE') return describeAuditSnapshot(log.before);
  const before = log.before || {};
  const after = log.after || {};
  const changes: string[] = [];
  for (const key of new Set([...Object.keys(before), ...Object.keys(after)])) {
    if (key === 'id') continue;
    if (before[key] !== after[key]) {
      changes.push(
        `${AUDIT_FIELD_LABELS[key] || key}: ${formatAuditValue(key, before[key])} → ${formatAuditValue(key, after[key])}`,
      );
    }
  }
  return changes.length ? changes.join('; ') : describeAuditSnapshot(after);
};

const formatAuditDateTime = (value: string): string => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const MONTH_OPTIONS = [
  { value: '1', label: 'Январь' },
  { value: '2', label: 'Февраль' },
  { value: '3', label: 'Март' },
  { value: '4', label: 'Апрель' },
  { value: '5', label: 'Май' },
  { value: '6', label: 'Июнь' },
  { value: '7', label: 'Июль' },
  { value: '8', label: 'Август' },
  { value: '9', label: 'Сентябрь' },
  { value: '10', label: 'Октябрь' },
  { value: '11', label: 'Ноябрь' },
  { value: '12', label: 'Декабрь' },
];

const rub = (n: number) => n.toLocaleString('ru-RU');
const qty = (n: number) => n.toLocaleString('ru-RU');

function waterDiagnostics(w: MetrologistWaterSummaryBlock) {
  return w.diagnosticsWater ?? { amount: 0, quantity: 0, accrual: 0 };
}

function waterAmountForRow(w: MetrologistWaterSummaryBlock, rowKey: string): number {
  const dw = waterDiagnostics(w);
  switch (rowKey) {
    case 'verification':
      return w.verification.amount;
    case 'diagnosticsWater':
      return dw.amount;
    case 'waterCounters':
      return w.waterCounters.amount;
    case 'replacement':
      return w.replacement.amount;
    case 'sealing':
      return w.sealing.amount;
    default:
      return 0;
  }
}

function waterQtyForRow(w: MetrologistWaterSummaryBlock, rowKey: string): number {
  const dw = waterDiagnostics(w);
  switch (rowKey) {
    case 'verification':
      return w.verification.quantity;
    case 'diagnosticsWater':
      return dw.quantity;
    case 'waterCounters':
      return w.waterCounters.quantity;
    case 'replacement':
      return w.replacement.quantity;
    case 'sealing':
      return w.sealing.quantity;
    default:
      return 0;
  }
}

function waterAccrualForRow(w: MetrologistWaterSummaryBlock, rowKey: string): number {
  const dw = waterDiagnostics(w);
  switch (rowKey) {
    case 'verification':
      return w.verification.accrual ?? 0;
    case 'diagnosticsWater':
      return dw.accrual;
    case 'waterCounters':
      return w.waterCounters.accrual ?? 0;
    case 'replacement':
      return w.replacement.accrual ?? 0;
    case 'sealing':
      return w.sealing.accrual ?? 0;
    default:
      return 0;
  }
}

function heatQtyForRow(h: MetrologistHeatSummaryBlock, rowKey: string): number {
  switch (rowKey) {
    case 'verification':
      return h.verification.quantity;
    case 'mounting':
      return h.mounting.quantity;
    case 'sale':
      return h.sale.quantity;
    case 'batteryReplacement':
      return h.batteryReplacement.quantity;
    default:
      return 0;
  }
}

function heatAmountForRow(h: MetrologistHeatSummaryBlock, rowKey: string): number {
  switch (rowKey) {
    case 'verification':
      return h.verification.amount ?? 0;
    case 'mounting':
      return h.mounting.amount ?? 0;
    case 'sale':
      return h.sale.amount ?? 0;
    case 'batteryReplacement':
      return h.batteryReplacement.amount ?? 0;
    default:
      return 0;
  }
}

function heatAccrualForRow(h: MetrologistHeatSummaryBlock, rowKey: string): number {
  switch (rowKey) {
    case 'verification':
      return h.verification.accrual ?? 0;
    case 'mounting':
      return h.mounting.accrual ?? 0;
    case 'sale':
      return h.sale.accrual ?? 0;
    case 'batteryReplacement':
      return h.batteryReplacement.accrual ?? 0;
    default:
      return 0;
  }
}

const parseFilledServiceCost = (value: string): number | undefined => {
  const trimmed = String(value ?? '').trim();
  if (trimmed === '') return undefined;
  const num = Number(trimmed);
  if (Number.isNaN(num)) return undefined;
  return num;
};

type MetrologistFinanceRow = {
  employeeId: string;
  fullName: string;
  salaryAccrued: number;
  unsurrenderedRevenue: number;
  cashRevenue: number;
  bankRevenue: number;
  totalRevenue: number;
  gsm: number;
};

type CityCashSummary = {
  cashInRegister: number;
  surrenderedRevenueAllTime: number;
  leftToHandoverPeriod: number;
};

const ReportPage: React.FC = () => {
  const extractApiErrorMessage = async (error: unknown): Promise<string> => {
    const responseData = (error as { response?: { data?: unknown } })?.response?.data;
    if (responseData instanceof Blob) {
      const text = await responseData.text();
      if (!text) return '';
      try {
        const parsed = JSON.parse(text) as { message?: string | string[]; error?: string };
        if (Array.isArray(parsed.message)) return parsed.message.filter(Boolean).join(', ');
        if (typeof parsed.message === 'string') return parsed.message;
        if (typeof parsed.error === 'string') return parsed.error;
      } catch {
        return text;
      }
      return text;
    }
    if (typeof responseData === 'string') {
      return responseData;
    }
    if (responseData && typeof responseData === 'object') {
      const payload = responseData as { message?: string | string[]; error?: string };
      if (Array.isArray(payload.message)) return payload.message.filter(Boolean).join(', ');
      if (typeof payload.message === 'string') return payload.message;
      if (typeof payload.error === 'string') return payload.error;
    }
    if (error instanceof Error && error.message) {
      return error.message;
    }
    return '';
  };

  const { user } = useAuth();
  const isSalaryUser = user?.type === 'salary';
  const isAdminUser = user?.type === 'admin';
  const [activeTab, setActiveTab] = useState<ReportTab>('metrologists');
  const [services, setServices] = useState<SalaryService[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingServiceId, setEditingServiceId] = useState<string | null>(null);
  const [serviceForm, setServiceForm] = useState<ServiceFormState>(initialForm);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [employeesLoading, setEmployeesLoading] = useState(false);
  const [isEmployeeFormOpen, setIsEmployeeFormOpen] = useState(false);
  const [editingEmployeeId, setEditingEmployeeId] = useState<string | null>(null);
  const [employeeForm, setEmployeeForm] = useState<EmployeeFormState>(initialEmployeeForm);
  const [metrologists, setMetrologists] = useState<Employee[]>([]);
  const [metrologistLoading, setMetrologistLoading] = useState(false);
  const [selectedMetrologistCity, setSelectedMetrologistCity] = useState('');
  const [selectedMetrologistId, setSelectedMetrologistId] = useState('');
  const [periodFrom, setPeriodFrom] = useState(getMonthStartDate());
  const [periodTo, setPeriodTo] = useState(getTodayDate());
  const [metrologistWork, setMetrologistWork] = useState<MetrologistWork | null>(null);
  const [metrologistPage, setMetrologistPage] = useState(1);
  const metrologistPageSize = 50;
  const [isCityAggregateView, setIsCityAggregateView] = useState(false);
  const [cityAggregateAllDeals, setCityAggregateAllDeals] = useState<MetrologistWork['work']>([]);
  const [metrologistPlanTotal, setMetrologistPlanTotal] = useState(0);
  const [metrologistExpensesTotal, setMetrologistExpensesTotal] = useState(0);
  const [metrologistFinanceRows, setMetrologistFinanceRows] = useState<MetrologistFinanceRow[]>([]);
  const [cityCashSummary, setCityCashSummary] = useState<CityCashSummary | null>(null);
  const [excelExportEmployeeIds, setExcelExportEmployeeIds] = useState<string[]>([]);
  const [bonusExtraForm, setBonusExtraForm] = useState<DevelopmentEntryFormState>({
    ...initialDevelopmentEntryForm,
    date: getTodayDate(),
  });
  const [gsmForm, setGsmForm] = useState<DevelopmentEntryFormState>({
    ...initialDevelopmentEntryForm,
    date: getTodayDate(),
  });
  const [revenueHandoverForm, setRevenueHandoverForm] = useState<DevelopmentEntryFormState>({
    ...initialDevelopmentEntryForm,
    date: getTodayDate(),
  });
  const [expenseForm, setExpenseForm] = useState<ExpenseFormState>({
    ...initialExpenseForm,
    date: getTodayDate(),
  });
  const [planForm, setPlanForm] = useState<PlanFormState>(initialPlanForm);
  const [businessTripForm, setBusinessTripForm] = useState<BusinessTripFormState>({
    ...initialBusinessTripForm,
    city: selectedMetrologistCity,
    employeeId: selectedMetrologistId,
    from: getTodayDate(),
    to: getTodayDate(),
  });
  const [householdForm, setHouseholdForm] = useState<HouseholdFormState>({
    ...initialHouseholdForm,
    city: selectedMetrologistCity,
    date: getTodayDate(),
  });
  const [householdRows, setHouseholdRows] = useState<HouseholdStockMovement[]>([]);
  const [householdSummary, setHouseholdSummary] = useState<HouseholdStockBalances | null>(null);
  const [householdSummaryLoading, setHouseholdSummaryLoading] = useState(false);
  const [householdSummaryVisible, setHouseholdSummaryVisible] = useState(false);
  const [householdOperationsPage, setHouseholdOperationsPage] = useState(1);
  const householdSummaryRequestIdRef = useRef(0);
  const [editingHouseholdId, setEditingHouseholdId] = useState<string | null>(null);
  const [serviceCostForm, setServiceCostForm] = useState<ServiceCostFormState>(initialServiceCostForm);
  const [bonusExtraRows, setBonusExtraRows] = useState<BonusExtraEntry[]>([]);
  const [gsmRows, setGsmRows] = useState<GsmEntry[]>([]);
  const [revenueHandoverRows, setRevenueHandoverRows] = useState<RevenueHandoverEntry[]>([]);
  const [expenseRows, setExpenseRows] = useState<ExpenseEntry[]>([]);
  const [planRows, setPlanRows] = useState<PlanEntry[]>([]);
  const [businessTripRows, setBusinessTripRows] = useState<BusinessTrip[]>([]);
  const [serviceCostRows, setServiceCostRows] = useState<ServiceCostEntry[]>([]);
  const [developmentLoading, setDevelopmentLoading] = useState(false);
  const [auditLogs, setAuditLogs] = useState<ReportEntryAuditLog[]>([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditFilterEntityType, setAuditFilterEntityType] = useState<ReportEntryEntityType | ''>('');
  const [auditFilterCity, setAuditFilterCity] = useState('');
  const [auditFilterActorLogin, setAuditFilterActorLogin] = useState('');
  const [auditFilterActorType, setAuditFilterActorType] = useState<'admin' | 'salary' | ''>('salary');
  const [auditFilterFrom, setAuditFilterFrom] = useState(getMonthStartDate());
  const [auditFilterTo, setAuditFilterTo] = useState(getTodayDate());
  const [editingBonusExtraId, setEditingBonusExtraId] = useState<string | null>(null);
  const [editingGsmId, setEditingGsmId] = useState<string | null>(null);
  const [editingRevenueHandoverId, setEditingRevenueHandoverId] = useState<string | null>(null);
  const [editingExpenseId, setEditingExpenseId] = useState<string | null>(null);
  const [editingPlanId, setEditingPlanId] = useState<string | null>(null);
  const [editingBusinessTripId, setEditingBusinessTripId] = useState<string | null>(null);
  const [editingServiceCostId, setEditingServiceCostId] = useState<string | null>(null);
  const householdOperationsPageSize = 25;


  const metrologistCityOptions = useMemo(() => {
    const cities = new Set(
      [...metrologists.map((employee) => employee.city), ...businessTripRows.map((trip) => trip.city)]
        .filter((city): city is string => Boolean(city)),
    );

    const fromCatalog = CITY_OPTIONS.filter((option) => cities.has(option.value));
    const catalogValues = new Set(fromCatalog.map((option) => option.value));
    const extraCities = Array.from(cities)
      .filter((city) => !catalogValues.has(city))
      .sort((a, b) => a.localeCompare(b, 'ru'))
      .map((city) => ({ label: city, value: city }));

    return [...fromCatalog, ...extraCities];
  }, [metrologists, businessTripRows]);

  const filteredMetrologists = useMemo(() => {
    if (!selectedMetrologistCity) {
      return [];
    }
    const fromDate = new Date(`${periodFrom}T00:00:00.000Z`);
    const toDate = new Date(`${periodTo}T23:59:59.999Z`);
    const hasValidPeriod = !Number.isNaN(fromDate.getTime()) && !Number.isNaN(toDate.getTime());
    const cityKey = selectedMetrologistCity.trim().toLowerCase();
    return metrologists.filter((employee) => {
      const homeCityMatch = (employee.city || '').trim().toLowerCase() === cityKey;
      if (homeCityMatch) {
        return true;
      }
      if (!hasValidPeriod) {
        return false;
      }
      return businessTripRows.some((trip) => {
        if (trip.employeeId !== employee.id) return false;
        if ((trip.city || '').trim().toLowerCase() !== cityKey) return false;
        const tripFrom = new Date(trip.from);
        const tripTo = new Date(trip.to);
        if (Number.isNaN(tripFrom.getTime()) || Number.isNaN(tripTo.getTime())) return false;
        return tripFrom.getTime() <= toDate.getTime() && tripTo.getTime() >= fromDate.getTime();
      });
    });
  }, [metrologists, businessTripRows, selectedMetrologistCity, periodFrom, periodTo]);

  const developmentCityOptions = metrologistCityOptions;
  const filteredBusinessTripRows = useMemo(() => {
    return businessTripRows.filter((row) => {
      if (selectedMetrologistCity && row.city !== selectedMetrologistCity) return false;
      if (selectedMetrologistId && row.employeeId !== selectedMetrologistId) return false;
      return true;
    });
  }, [businessTripRows, selectedMetrologistCity, selectedMetrologistId]);
  const auditEntityTypeOptions = useMemo(
    () => [
      { label: 'Все разделы', value: '' },
      ...(Object.entries(REPORT_ENTRY_ENTITY_LABELS) as Array<[ReportEntryEntityType, string]>).map(
        ([value, label]) => ({ label, value }),
      ),
    ],
    [],
  );
  const auditActorTypeOptions = useMemo(
    () => [
      { label: 'Зарплата', value: 'salary' },
      { label: 'Все роли', value: '' },
      { label: 'Админ', value: 'admin' },
    ],
    [],
  );
  /** Общий список сотрудников по выбранному городу — используется на всех вкладках. */
  const metrologistsBySharedCity = filteredMetrologists;
  const displayedAccruedTotal = useMemo(() => {
    if (!metrologistWork) return 0;
    if (metrologistFinanceRows.length > 0) {
      return Number(metrologistFinanceRows.reduce((sum, row) => sum + Number(row.salaryAccrued || 0), 0).toFixed(2));
    }
    return Number(Number(metrologistWork.totalReward || 0).toFixed(2));
  }, [metrologistWork, metrologistFinanceRows]);

  const applySharedCity = (city: string) => {
    setSelectedMetrologistCity(city);
    setMetrologistPage(1);
    setMetrologistWork(null);
    setExcelExportEmployeeIds([]);
    if (!city) {
      setSelectedMetrologistId('');
    }
  };

  const applySharedEmployee = (employeeId: string) => {
    setSelectedMetrologistId(employeeId);
    setMetrologistPage(1);
    setMetrologistWork(null);
  };

  const developmentFormWithSharedSelection = (
    overrides: Partial<DevelopmentEntryFormState> = {},
  ): DevelopmentEntryFormState => ({
    ...initialDevelopmentEntryForm,
    date: getTodayDate(),
    city: selectedMetrologistCity,
    employeeId: selectedMetrologistId,
    ...overrides,
  });

  const expenseFormWithSharedSelection = (
    overrides: Partial<ExpenseFormState> = {},
  ): ExpenseFormState => ({
    ...initialExpenseForm,
    date: getTodayDate(),
    city: selectedMetrologistCity,
    employeeId: selectedMetrologistId,
    ...overrides,
  });

  const businessTripFormWithSharedSelection = (
    overrides: Partial<BusinessTripFormState> = {},
  ): BusinessTripFormState => ({
    ...initialBusinessTripForm,
    city: selectedMetrologistCity,
    employeeId: selectedMetrologistId,
    from: getTodayDate(),
    to: getTodayDate(),
    ...overrides,
  });

  const getRevenueSplitFromSummary = (summary: MetrologistWork['summary']) => {
    const cashRevenue =
      Number(summary.water.nonBank.revenueTotal || 0) +
      Number(summary.heat.nonBank.revenueTotal || 0) +
      Number(summary.gas.nonBank.revenueTotal || 0);
    const bankRevenue =
      Number(summary.water.bankAccount.revenueTotal || 0) +
      Number(summary.heat.bankAccount.revenueTotal || 0) +
      Number(summary.gas.bankAccount.revenueTotal || 0);
    return {
      cashRevenue: Number(cashRevenue.toFixed(2)),
      bankRevenue: Number(bankRevenue.toFixed(2)),
      totalRevenue: Number((cashRevenue + bankRevenue).toFixed(2)),
    };
  };

  const buildFinanceRow = (params: {
    employeeId: string;
    fullName: string;
    summary: MetrologistWork['summary'];
    salaryAccrued: number;
    handoverTotal: number;
    gsmTotal: number;
    bonusExtraTotal: number;
  }): MetrologistFinanceRow => {
    const split = getRevenueSplitFromSummary(params.summary);
    const salaryWithExtras =
      Number(params.salaryAccrued || 0) + Number(params.gsmTotal || 0) + Number(params.bonusExtraTotal || 0);
    return {
      employeeId: params.employeeId,
      fullName: params.fullName,
      salaryAccrued: Number(salaryWithExtras.toFixed(2)),
      unsurrenderedRevenue: Number(Math.max(0, split.cashRevenue - params.handoverTotal).toFixed(2)),
      cashRevenue: split.cashRevenue,
      bankRevenue: split.bankRevenue,
      totalRevenue: split.totalRevenue,
      gsm: Number(params.gsmTotal.toFixed(2)),
    };
  };


  const getLatestActualRateLabel = (variant: SalaryService['variants'][number]): string => {
    const monthRates = (variant.monthRates || []).filter((r) => r.isActive !== false);
    if (monthRates.length > 0) {
      const latest = [...monthRates].sort((a, b) => {
        const ai = a.year * 100 + a.month;
        const bi = b.year * 100 + b.month;
        return bi - ai;
      })[0];
      const rateValue =
        latest.rateType === 'fixed'
          ? `${(latest.fixedPrice ?? 0).toLocaleString('ru-RU')} руб`
          : `${(latest.percentage ?? 0).toLocaleString('ru-RU')}%`;
      return rateValue;
    }
    return variant.rateType === 'fixed'
      ? `${(variant.fixedPrice ?? 0).toLocaleString('ru-RU')} руб`
      : `${(variant.percentage ?? 0).toLocaleString('ru-RU')}%`;
  };

  const loadSalaryServices = async () => {
    try {
      setLoading(true);
      const data = await reportApi.getSalaryServices();
      setServices(data);
    } catch (error) {
      console.error('Error loading salary services:', error);
      alert('Не удалось загрузить услуги для зарплаты');
    } finally {
      setLoading(false);
    }
  };

  const loadEmployees = async () => {
    try {
      setEmployeesLoading(true);
      const data = await reportApi.getEmployees();
      setEmployees(data);
    } catch (error) {
      console.error('Error loading employees:', error);
      alert('Не удалось загрузить сотрудников');
    } finally {
      setEmployeesLoading(false);
    }
  };

  const loadMetrologists = async () => {
    try {
      setMetrologistLoading(true);
      const [data, trips] = await Promise.all([
        reportApi.getEmployees({ role: 'METROLOGIST', isActive: true }),
        reportApi.listBusinessTrips(),
      ]);
      setMetrologists(data);
      setBusinessTripRows(trips);
    } catch (error) {
      console.error('Error loading metrologists:', error);
      alert('Не удалось загрузить список метрологов');
    } finally {
      setMetrologistLoading(false);
    }
  };

  const loadDevelopmentRows = async () => {
    try {
      setDevelopmentLoading(true);
      const [bonus, gsm, handovers, expenses, plans, businessTrips, serviceCosts, household] = await Promise.all([
        reportApi.listBonusExtras(),
        reportApi.listGsmEntries(),
        reportApi.listRevenueHandovers(),
        reportApi.listExpenses(),
        reportApi.listPlans(),
        reportApi.listBusinessTrips(),
        reportApi.listServiceCosts(),
        reportApi.listHouseholdStockMovements(),
      ]);
      setBonusExtraRows(bonus);
      setGsmRows(gsm);
      setRevenueHandoverRows(handovers);
      setExpenseRows(expenses);
      setPlanRows(plans);
      setBusinessTripRows(businessTrips);
      setServiceCostRows(serviceCosts);
      setHouseholdRows(household);
    } catch (error) {
      console.error('Error loading development entries:', error);
      alert('Не удалось загрузить записи для вкладок');
    } finally {
      setDevelopmentLoading(false);
    }
  };

  const loadAuditLogs = async () => {
    try {
      setAuditLoading(true);
      const rows = await reportApi.listEntryAuditLog({
        entityType: auditFilterEntityType || undefined,
        city: auditFilterCity || undefined,
        actorLogin: auditFilterActorLogin.trim() || undefined,
        actorType: auditFilterActorType || undefined,
        from: auditFilterFrom || undefined,
        to: auditFilterTo || undefined,
      });
      setAuditLogs(rows);
    } catch (error) {
      console.error('Error loading audit logs:', error);
      alert('Не удалось загрузить журнал изменений');
    } finally {
      setAuditLoading(false);
    }
  };

  const handleCreateBonusExtra = async () => {
    if (!bonusExtraForm.city || !bonusExtraForm.employeeId || !bonusExtraForm.amount || !bonusExtraForm.date) {
      alert('Заполните город, сотрудника, сумму и дату');
      return;
    }
    try {
      setDevelopmentLoading(true);
      if (editingBonusExtraId) {
        await reportApi.updateBonusExtra(editingBonusExtraId, {
          city: bonusExtraForm.city,
          employeeId: bonusExtraForm.employeeId,
          amount: Number(bonusExtraForm.amount),
          date: bonusExtraForm.date,
          comment: bonusExtraForm.comment.trim() || undefined,
        });
      } else {
        await reportApi.createBonusExtra({
          city: bonusExtraForm.city,
          employeeId: bonusExtraForm.employeeId,
          amount: Number(bonusExtraForm.amount),
          date: bonusExtraForm.date,
          comment: bonusExtraForm.comment.trim() || undefined,
        });
      }
      setEditingBonusExtraId(null);
      setBonusExtraForm(developmentFormWithSharedSelection());
      await loadDevelopmentRows();
    } catch (error) {
      console.error('Error creating bonus/extra entry:', error);
      alert(editingBonusExtraId ? 'Не удалось обновить запись Доп начисления' : 'Не удалось добавить запись Доп начисления');
    } finally {
      setDevelopmentLoading(false);
    }
  };

  const handleEditBonusExtra = (row: BonusExtraEntry) => {
    setEditingBonusExtraId(row.id);
    setBonusExtraForm({
      city: row.city || '',
      employeeId: row.employeeId || '',
      amount: String(Number(row.amount || 0)),
      date: row.date ? new Date(row.date).toISOString().slice(0, 10) : getTodayDate(),
      comment: row.comment || '',
    });
    if (row.city) applySharedCity(row.city);
    if (row.employeeId) applySharedEmployee(row.employeeId);
  };

  const handleDeleteBonusExtra = async (id: string) => {
    if (!window.confirm('Удалить запись Доп начисления?')) return;
    try {
      setDevelopmentLoading(true);
      await reportApi.deleteBonusExtra(id);
      if (editingBonusExtraId === id) {
        setEditingBonusExtraId(null);
        setBonusExtraForm(developmentFormWithSharedSelection());
      }
      await loadDevelopmentRows();
    } catch (error) {
      console.error('Error deleting bonus/extra entry:', error);
      alert('Не удалось удалить запись Доп начисления');
    } finally {
      setDevelopmentLoading(false);
    }
  };

  const handleCreateGsm = async () => {
    if (!gsmForm.city || !gsmForm.employeeId || !gsmForm.amount || !gsmForm.date) {
      alert('Заполните город, сотрудника, сумму и дату');
      return;
    }
    try {
      setDevelopmentLoading(true);
      if (editingGsmId) {
        await reportApi.updateGsmEntry(editingGsmId, {
          city: gsmForm.city,
          employeeId: gsmForm.employeeId,
          amount: Number(gsmForm.amount),
          date: gsmForm.date,
          comment: gsmForm.comment.trim() || undefined,
        });
      } else {
        await reportApi.createGsmEntry({
          city: gsmForm.city,
          employeeId: gsmForm.employeeId,
          amount: Number(gsmForm.amount),
          date: gsmForm.date,
          comment: gsmForm.comment.trim() || undefined,
        });
      }
      setEditingGsmId(null);
      setGsmForm(developmentFormWithSharedSelection());
      await loadDevelopmentRows();
    } catch (error) {
      console.error('Error creating GSM entry:', error);
      alert(editingGsmId ? 'Не удалось обновить запись ГСМ' : 'Не удалось добавить запись ГСМ');
    } finally {
      setDevelopmentLoading(false);
    }
  };

  const handleEditGsm = (row: GsmEntry) => {
    setEditingGsmId(row.id);
    setGsmForm({
      city: row.city || '',
      employeeId: row.employeeId || '',
      amount: String(Number(row.amount || 0)),
      date: row.date ? new Date(row.date).toISOString().slice(0, 10) : getTodayDate(),
      comment: row.comment || '',
    });
    if (row.city) applySharedCity(row.city);
    if (row.employeeId) applySharedEmployee(row.employeeId);
  };

  const handleDeleteGsm = async (id: string) => {
    if (!window.confirm('Удалить запись ГСМ?')) return;
    try {
      setDevelopmentLoading(true);
      await reportApi.deleteGsmEntry(id);
      if (editingGsmId === id) {
        setEditingGsmId(null);
        setGsmForm(developmentFormWithSharedSelection());
      }
      await loadDevelopmentRows();
    } catch (error) {
      console.error('Error deleting GSM entry:', error);
      alert('Не удалось удалить запись ГСМ');
    } finally {
      setDevelopmentLoading(false);
    }
  };

  const handleCreateRevenueHandover = async () => {
    if (!revenueHandoverForm.city || !revenueHandoverForm.amount || !revenueHandoverForm.date) {
      alert('Заполните город, сумму и дату');
      return;
    }
    try {
      setDevelopmentLoading(true);
      if (editingRevenueHandoverId) {
        await reportApi.updateRevenueHandover(editingRevenueHandoverId, {
          city: revenueHandoverForm.city,
          employeeId: revenueHandoverForm.employeeId || undefined,
          amount: Number(revenueHandoverForm.amount),
          date: revenueHandoverForm.date,
          comment: revenueHandoverForm.comment.trim() || undefined,
        });
      } else {
        await reportApi.createRevenueHandover({
          city: revenueHandoverForm.city,
          employeeId: revenueHandoverForm.employeeId || undefined,
          amount: Number(revenueHandoverForm.amount),
          date: revenueHandoverForm.date,
          comment: revenueHandoverForm.comment.trim() || undefined,
        });
      }
      setEditingRevenueHandoverId(null);
      setRevenueHandoverForm(developmentFormWithSharedSelection());
      await loadDevelopmentRows();
    } catch (error) {
      console.error('Error creating revenue handover entry:', error);
      alert(
        editingRevenueHandoverId
          ? 'Не удалось обновить запись Сдача выручки'
          : 'Не удалось добавить запись Сдача выручки',
      );
    } finally {
      setDevelopmentLoading(false);
    }
  };

  const handleEditRevenueHandover = (row: RevenueHandoverEntry) => {
    setEditingRevenueHandoverId(row.id);
    setRevenueHandoverForm({
      city: row.city || '',
      employeeId: row.employeeId || '',
      amount: String(Number(row.amount || 0)),
      date: row.date ? new Date(row.date).toISOString().slice(0, 10) : getTodayDate(),
      comment: row.comment || '',
    });
    if (row.city) applySharedCity(row.city);
    if (row.employeeId) applySharedEmployee(row.employeeId);
  };

  const handleDeleteRevenueHandover = async (id: string) => {
    if (!window.confirm('Удалить запись Сдача выручки?')) return;
    try {
      setDevelopmentLoading(true);
      await reportApi.deleteRevenueHandover(id);
      if (editingRevenueHandoverId === id) {
        setEditingRevenueHandoverId(null);
        setRevenueHandoverForm(developmentFormWithSharedSelection());
      }
      await loadDevelopmentRows();
    } catch (error) {
      console.error('Error deleting revenue handover entry:', error);
      alert('Не удалось удалить запись Сдача выручки');
    } finally {
      setDevelopmentLoading(false);
    }
  };

  const handleCreateExpense = async () => {
    if (!expenseForm.city || !expenseForm.amount || !expenseForm.date) {
      alert('Заполните город, сумму и дату');
      return;
    }
    try {
      setDevelopmentLoading(true);
      if (editingExpenseId) {
        await reportApi.updateExpense(editingExpenseId, {
          city: expenseForm.city,
          employeeId: expenseForm.employeeId || undefined,
          paymentType: expenseForm.paymentType,
          amount: Number(expenseForm.amount),
          date: expenseForm.date,
          comment: expenseForm.comment.trim() || undefined,
        });
      } else {
        await reportApi.createExpense({
          city: expenseForm.city,
          employeeId: expenseForm.employeeId || undefined,
          paymentType: expenseForm.paymentType,
          amount: Number(expenseForm.amount),
          date: expenseForm.date,
          comment: expenseForm.comment.trim() || undefined,
        });
      }
      setEditingExpenseId(null);
      setExpenseForm(expenseFormWithSharedSelection({ paymentType: expenseForm.paymentType }));
      await loadDevelopmentRows();
    } catch (error) {
      console.error('Error creating expense entry:', error);
      alert(editingExpenseId ? 'Не удалось обновить запись Расходы' : 'Не удалось добавить запись Расходы');
    } finally {
      setDevelopmentLoading(false);
    }
  };

  const handleEditExpense = (row: ExpenseEntry) => {
    setEditingExpenseId(row.id);
    setExpenseForm({
      city: row.city || '',
      employeeId: row.employeeId || '',
      paymentType: row.paymentType === 'bank' ? 'bank' : 'cash',
      amount: String(Number(row.amount || 0)),
      date: row.date ? new Date(row.date).toISOString().slice(0, 10) : getTodayDate(),
      comment: row.comment || '',
    });
    if (row.city) applySharedCity(row.city);
    if (row.employeeId) applySharedEmployee(row.employeeId);
  };

  const handleDeleteExpense = async (id: string) => {
    if (!window.confirm('Удалить запись Расходы?')) return;
    try {
      setDevelopmentLoading(true);
      await reportApi.deleteExpense(id);
      if (editingExpenseId === id) {
        setEditingExpenseId(null);
        setExpenseForm(expenseFormWithSharedSelection());
      }
      await loadDevelopmentRows();
    } catch (error) {
      console.error('Error deleting expense entry:', error);
      alert('Не удалось удалить запись Расходы');
    } finally {
      setDevelopmentLoading(false);
    }
  };

  const handleUpsertPlan = async () => {
    if (!planForm.city || !planForm.month || !planForm.amount) {
      alert('Заполните город, месяц и сумму');
      return;
    }
    const [yearStr, monthStr] = planForm.month.split('-');
    const year = Number(yearStr);
    const month = Number(monthStr);
    if (!year || !month) {
      alert('Некорректный месяц плана');
      return;
    }
    try {
      setDevelopmentLoading(true);
      if (editingPlanId) {
        await reportApi.updatePlan(editingPlanId, {
          city: planForm.city,
          year,
          month,
          amount: Number(planForm.amount),
        });
      } else {
        await reportApi.upsertPlan({
          city: planForm.city,
          year,
          month,
          amount: Number(planForm.amount),
        });
      }
      setEditingPlanId(null);
      setPlanForm({ ...initialPlanForm, city: selectedMetrologistCity });
      await loadDevelopmentRows();
    } catch (error) {
      console.error('Error upserting plan entry:', error);
      alert(editingPlanId ? 'Не удалось обновить план' : 'Не удалось сохранить план');
    } finally {
      setDevelopmentLoading(false);
    }
  };

  const handleEditPlan = (row: PlanEntry) => {
    setEditingPlanId(row.id);
    setPlanForm({
      city: row.city || '',
      month: `${row.year}-${String(row.month).padStart(2, '0')}`,
      amount: String(Number(row.amount || 0)),
    });
    if (row.city) applySharedCity(row.city);
  };

  const handleDeletePlan = async (id: string) => {
    if (!window.confirm('Удалить план?')) return;
    try {
      setDevelopmentLoading(true);
      await reportApi.deletePlan(id);
      if (editingPlanId === id) {
        setEditingPlanId(null);
        setPlanForm({ ...initialPlanForm, city: selectedMetrologistCity });
      }
      await loadDevelopmentRows();
    } catch (error) {
      console.error('Error deleting plan entry:', error);
      alert('Не удалось удалить план');
    } finally {
      setDevelopmentLoading(false);
    }
  };

  const handleCreateBusinessTrip = async () => {
    if (!businessTripForm.city || !businessTripForm.employeeId || !businessTripForm.from || !businessTripForm.to) {
      alert('Заполните город командировки, сотрудника и диапазон дат');
      return;
    }
    try {
      setDevelopmentLoading(true);
      if (editingBusinessTripId) {
        await reportApi.updateBusinessTrip(editingBusinessTripId, {
          city: businessTripForm.city,
          employeeId: businessTripForm.employeeId,
          from: businessTripForm.from,
          to: businessTripForm.to,
          comment: businessTripForm.comment.trim() || undefined,
        });
      } else {
        await reportApi.createBusinessTrip({
          city: businessTripForm.city,
          employeeId: businessTripForm.employeeId,
          from: businessTripForm.from,
          to: businessTripForm.to,
          comment: businessTripForm.comment.trim() || undefined,
        });
      }
      setEditingBusinessTripId(null);
      setBusinessTripForm(businessTripFormWithSharedSelection());
      await loadDevelopmentRows();
      await loadMetrologists();
    } catch (error) {
      console.error('Error saving business trip:', error);
      const apiMessage = await extractApiErrorMessage(error);
      alert(apiMessage || (editingBusinessTripId ? 'Не удалось обновить командировку' : 'Не удалось добавить командировку'));
    } finally {
      setDevelopmentLoading(false);
    }
  };

  const handleEditBusinessTrip = (row: BusinessTrip) => {
    setEditingBusinessTripId(row.id);
    setBusinessTripForm({
      city: row.city || '',
      employeeId: row.employeeId || '',
      from: row.from ? new Date(row.from).toISOString().slice(0, 10) : getTodayDate(),
      to: row.to ? new Date(row.to).toISOString().slice(0, 10) : getTodayDate(),
      comment: row.comment || '',
    });
    if (row.city) applySharedCity(row.city);
    if (row.employeeId) applySharedEmployee(row.employeeId);
  };

  const handleDeleteBusinessTrip = async (id: string) => {
    if (!window.confirm('Удалить командировку?')) return;
    try {
      setDevelopmentLoading(true);
      await reportApi.deleteBusinessTrip(id);
      if (editingBusinessTripId === id) {
        setEditingBusinessTripId(null);
        setBusinessTripForm(businessTripFormWithSharedSelection());
      }
      await loadDevelopmentRows();
      await loadMetrologists();
    } catch (error) {
      console.error('Error deleting business trip:', error);
      alert('Не удалось удалить командировку');
    } finally {
      setDevelopmentLoading(false);
    }
  };

  const loadHouseholdSummary = async () => {
    if (!selectedMetrologistCity || !periodFrom || !periodTo) {
      alert('Выберите город и период');
      return;
    }
    const requestId = householdSummaryRequestIdRef.current + 1;
    householdSummaryRequestIdRef.current = requestId;
    try {
      setHouseholdSummaryLoading(true);
      const summary = await reportApi.getHouseholdStockBalances({
        city: selectedMetrologistCity,
        from: periodFrom,
        to: periodTo,
        employeeId: selectedMetrologistId || undefined,
      });
      if (householdSummaryRequestIdRef.current !== requestId) return;
      setHouseholdSummary(summary);
      setHouseholdSummaryVisible(true);
    } catch (error: any) {
      if (householdSummaryRequestIdRef.current !== requestId) return;
      console.error('Error loading household summary:', error);
      const apiMessage =
        error?.response?.data?.message ||
        error?.message ||
        'Не удалось рассчитать остатки хоз блока';
      alert(Array.isArray(apiMessage) ? apiMessage.join(', ') : String(apiMessage));
      setHouseholdSummary(null);
      setHouseholdSummaryVisible(false);
    } finally {
      if (householdSummaryRequestIdRef.current === requestId) {
        setHouseholdSummaryLoading(false);
      }
    }
  };

  const handleCreateHouseholdReceipt = async () => {
    if (!householdForm.city || !householdForm.quantity || !householdForm.date) {
      alert('Заполните город, количество и дату');
      return;
    }
    const quantity = Number(householdForm.quantity);
    if (!Number.isFinite(quantity) || quantity < 1) {
      alert('Количество должно быть не меньше 1');
      return;
    }
    if (
      (householdForm.operationType === 'issue' ||
        householdForm.operationType === 'return' ||
        householdForm.operationType === 'consume' ||
        householdForm.scope === 'employee') &&
      !householdForm.employeeId
    ) {
      alert('Для этой операции выберите метролога');
      return;
    }
    try {
      setDevelopmentLoading(true);
      const payload = {
        city: householdForm.city,
        itemType: householdForm.itemType,
        scope: householdForm.scope,
        direction: householdForm.direction,
        operationType: householdForm.operationType,
        employeeId: householdForm.employeeId || undefined,
        dealId: householdForm.dealId.trim() || undefined,
        quantity: Math.trunc(quantity),
        date: householdForm.date,
        comment: householdForm.comment.trim() || undefined,
      };
      if (editingHouseholdId) {
        await reportApi.updateHouseholdStockMovement(editingHouseholdId, payload);
      } else {
        await reportApi.createHouseholdStockMovement(payload);
      }
      setEditingHouseholdId(null);
      setHouseholdForm({
        ...initialHouseholdForm,
        city: selectedMetrologistCity,
        date: getTodayDate(),
      });
      await loadDevelopmentRows();
      if (selectedMetrologistCity && periodFrom && periodTo) {
        await loadHouseholdSummary();
      }
    } catch (error) {
      console.error('Error saving household movement:', error);
      alert(editingHouseholdId ? 'Не удалось обновить операцию' : 'Не удалось добавить операцию');
    } finally {
      setDevelopmentLoading(false);
    }
  };

  const handleEditHouseholdReceipt = (row: HouseholdStockMovement) => {
    setEditingHouseholdId(row.id);
    setHouseholdForm({
      city: row.city || '',
      employeeId: row.employeeId || '',
      itemType: row.itemType,
      scope: row.scope,
      direction: row.direction,
      operationType: row.operationType,
      quantity: String(Number(row.quantity || 0)),
      date: row.date ? new Date(row.date).toISOString().slice(0, 10) : getTodayDate(),
      dealId: row.dealId || '',
      comment: row.comment || '',
    });
    if (row.city) applySharedCity(row.city);
  };

  const handleDeleteHouseholdReceipt = async (row: HouseholdStockMovement) => {
    const operationLabel = HOUSEHOLD_OPERATION_LABELS[row.operationType] || row.operationType;
    if (!window.confirm(`Удалить операцию "${operationLabel}"?`)) return;
    try {
      setDevelopmentLoading(true);
      await reportApi.deleteHouseholdStockMovement(row.id);
      if (editingHouseholdId === row.id) {
        setEditingHouseholdId(null);
        setHouseholdForm({
          ...initialHouseholdForm,
          city: selectedMetrologistCity,
          date: getTodayDate(),
        });
      }
      await loadDevelopmentRows();
      if (selectedMetrologistCity && periodFrom && periodTo) {
        await loadHouseholdSummary();
      }
    } catch (error) {
      console.error('Error deleting household movement:', error);
      const backendMessageRaw = (error as any)?.response?.data?.message;
      const backendMessage = Array.isArray(backendMessageRaw) ? backendMessageRaw.join('; ') : backendMessageRaw;
      const canForceAdjust =
        typeof backendMessage === 'string' && backendMessage.includes('автокорректировкой');
      if (canForceAdjust) {
        const confirmed = window.confirm(
          `${backendMessage}\n\nНажмите "ОК", чтобы удалить и автоматически добавить корректировку.`,
        );
        if (confirmed) {
          try {
            await reportApi.deleteHouseholdStockMovement(row.id, { forceAdjust: true });
            if (editingHouseholdId === row.id) {
              setEditingHouseholdId(null);
              setHouseholdForm({
                ...initialHouseholdForm,
                city: selectedMetrologistCity,
                date: getTodayDate(),
              });
            }
            await loadDevelopmentRows();
            if (selectedMetrologistCity && periodFrom && periodTo) {
              await loadHouseholdSummary();
            }
            return;
          } catch (forceError) {
            console.error('Error force deleting household movement:', forceError);
            const forceMessageRaw = (forceError as any)?.response?.data?.message;
            const forceMessage = Array.isArray(forceMessageRaw) ? forceMessageRaw.join('; ') : forceMessageRaw;
            alert(typeof forceMessage === 'string' ? forceMessage : 'Не удалось удалить операцию даже с корректировкой');
          }
        }
      } else {
        alert(typeof backendMessage === 'string' ? backendMessage : 'Не удалось удалить операцию');
      }
    } finally {
      setDevelopmentLoading(false);
    }
  };

  const handleRefreshHouseholdWithRecalculate = async () => {
    if (!selectedMetrologistCity || !periodFrom || !periodTo) {
      alert('Выберите город и период');
      return;
    }
    try {
      setDevelopmentLoading(true);
      setHouseholdSummaryLoading(true);
      await reportApi.recalculateHouseholdStockConsume({
        city: selectedMetrologistCity,
        from: periodFrom,
        to: periodTo,
        employeeId: selectedMetrologistId || undefined,
      });
      await loadDevelopmentRows();
      await loadHouseholdSummary();
    } catch (error) {
      console.error('Error refreshing household balances:', error);
      alert('Не удалось обновить остатки');
    } finally {
      setDevelopmentLoading(false);
      setHouseholdSummaryLoading(false);
    }
  };

  const handleUpsertServiceCost = async () => {
    const cities = (serviceCostForm.cities || []).map((c) => String(c || '').trim()).filter(Boolean);
    if (!cities.length || !serviceCostForm.month) {
      alert('Выберите хотя бы один город и укажите месяц');
      return;
    }
    const [yearStr, monthStr] = serviceCostForm.month.split('-');
    const year = Number(yearStr);
    const month = Number(monthStr);
    if (!year || !month) {
      alert('Некорректный месяц себестоимости');
      return;
    }
    const costKeys: ServiceCostFieldKey[] = [
      'waterVerification',
      'waterReplacement',
      'waterSale',
      'waterSealing',
      'waterDiagnostics',
      'heatVerification',
      'heatMounting',
      'heatSale',
      'heatBatteryReplacement',
      'gasVerification',
      'bankReceiptTaxPercent',
      'perRequestExpense',
      'dispatcherExpense',
    ];
    const costsPayload: {
      year: number;
      month: number;
    } & Partial<Record<ServiceCostFieldKey, number>> = {
      year,
      month,
    };
    for (const key of costKeys) {
      const parsed = parseFilledServiceCost(serviceCostForm[key]);
      if (parsed !== undefined) {
        costsPayload[key] = parsed;
      }
    }
    if (Object.keys(costsPayload).length <= 2) {
      alert('Заполните хотя бы одно поле себестоимости');
      return;
    }
    try {
      setDevelopmentLoading(true);
      // Apply filled fields to every selected city; empty fields keep previous values.
      await Promise.all(cities.map((city) => reportApi.upsertServiceCost({ ...costsPayload, city })));
      setEditingServiceCostId(null);
      setServiceCostForm(initialServiceCostForm);
      await loadDevelopmentRows();
    } catch (error) {
      console.error('Error upserting service cost entry:', error);
      alert(editingServiceCostId ? 'Не удалось обновить себестоимость' : 'Не удалось сохранить себестоимость');
    } finally {
      setDevelopmentLoading(false);
    }
  };

  const handleEditServiceCost = (row: ServiceCostEntry) => {
    setEditingServiceCostId(row.id);
    setServiceCostForm({
      cities: row.city ? [row.city] : [],
      month: `${row.year}-${String(row.month).padStart(2, '0')}`,
      waterVerification: String(Number(row.waterVerification || 0)),
      waterReplacement: String(Number(row.waterReplacement || 0)),
      waterSale: String(Number(row.waterSale || 0)),
      waterSealing: String(Number(row.waterSealing || 0)),
      waterDiagnostics: String(Number(row.waterDiagnostics || 0)),
      heatVerification: String(Number(row.heatVerification || 0)),
      heatMounting: String(Number(row.heatMounting || 0)),
      heatSale: String(Number(row.heatSale || 0)),
      heatBatteryReplacement: String(Number(row.heatBatteryReplacement || 0)),
      gasVerification: String(Number(row.gasVerification || 0)),
      bankReceiptTaxPercent: String(Number(row.bankReceiptTaxPercent || 0)),
      perRequestExpense: String(Number(row.perRequestExpense || 0)),
      dispatcherExpense: String(Number(row.dispatcherExpense || 0)),
    });
  };

  const handleDeleteServiceCost = async (id: string) => {
    if (!window.confirm('Удалить запись себестоимости?')) return;
    try {
      setDevelopmentLoading(true);
      await reportApi.deleteServiceCost(id);
      if (editingServiceCostId === id) {
        setEditingServiceCostId(null);
        setServiceCostForm(initialServiceCostForm);
      }
      await loadDevelopmentRows();
    } catch (error) {
      console.error('Error deleting service cost entry:', error);
      alert('Не удалось удалить себестоимость');
    } finally {
      setDevelopmentLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab !== 'householdDev') {
      householdSummaryRequestIdRef.current += 1;
      setHouseholdSummaryLoading(false);
      setHouseholdSummaryVisible(false);
    }
  }, [activeTab]);

  useEffect(() => {
    setHouseholdOperationsPage(1);
  }, [selectedMetrologistCity, selectedMetrologistId]);

  useEffect(() => {
    if (activeTab === 'householdDev') {
      setHouseholdSummaryVisible(false);
    }
  }, [activeTab, selectedMetrologistCity, selectedMetrologistId, periodFrom, periodTo]);

  useEffect(() => {
    if (isSalaryUser && activeTab === 'products') {
      setActiveTab('metrologists');
    }
    if (!isAdminUser && activeTab === 'auditConsole') {
      setActiveTab('metrologists');
    }
  }, [activeTab, isSalaryUser, isAdminUser]);

  useEffect(() => {
    if (activeTab === 'products') {
      loadSalaryServices();
    }
    if (activeTab === 'employees') {
      loadEmployees();
    }
    if (activeTab === 'metrologists') {
      loadMetrologists();
    }
    if (
      activeTab === 'bonusExtras' ||
      activeTab === 'gsmDev' ||
      activeTab === 'revenueHandovers' ||
      activeTab === 'expensesDev' ||
      activeTab === 'plansDev' ||
      activeTab === 'businessTripsDev' ||
      activeTab === 'serviceCostsDev' ||
      activeTab === 'householdDev'
    ) {
      loadMetrologists();
      loadDevelopmentRows();
    }
    if (activeTab === 'auditConsole' && isAdminUser) {
      void loadAuditLogs();
    }
  }, [activeTab, isAdminUser]);

  useEffect(() => {
    if (!selectedMetrologistCity) {
      setSelectedMetrologistId('');
      return;
    }

    // Allow manual clear via Dropdown "x" without forcing first employee back.
    if (!selectedMetrologistId) {
      return;
    }

    // In business trips tab we allow selecting an employee from any city.
    if (activeTab === 'businessTripsDev') {
      return;
    }

    if (!filteredMetrologists.some((employee) => employee.id === selectedMetrologistId)) {
      setSelectedMetrologistId(filteredMetrologists[0]?.id || '');
    }
  }, [activeTab, filteredMetrologists, selectedMetrologistCity, selectedMetrologistId]);

  // Общий город/сотрудник переносятся между вкладками (кроме режима редактирования записи).
  useEffect(() => {
    const city = selectedMetrologistCity;
    const employeeId = selectedMetrologistId;

    if (!editingBonusExtraId) {
      setBonusExtraForm((prev) =>
        prev.city === city && prev.employeeId === employeeId ? prev : { ...prev, city, employeeId },
      );
    }
    if (!editingGsmId) {
      setGsmForm((prev) =>
        prev.city === city && prev.employeeId === employeeId ? prev : { ...prev, city, employeeId },
      );
    }
    if (!editingRevenueHandoverId) {
      setRevenueHandoverForm((prev) =>
        prev.city === city && prev.employeeId === employeeId ? prev : { ...prev, city, employeeId },
      );
    }
    if (!editingExpenseId) {
      setExpenseForm((prev) => {
        if (prev.city !== city) {
          return { ...prev, city, employeeId };
        }
        // Город тот же: сотрудника не форсим, чтобы можно было оставить пустым.
        return prev;
      });
    }
    if (!editingPlanId) {
      setPlanForm((prev) => (prev.city === city ? prev : { ...prev, city }));
    }
    if (!editingBusinessTripId) {
      setBusinessTripForm((prev) =>
        prev.city === city && prev.employeeId === employeeId ? prev : { ...prev, city, employeeId },
      );
    }
    if (!editingHouseholdId) {
      setHouseholdForm((prev) => (prev.city === city ? prev : { ...prev, city }));
    }
    if (!editingServiceCostId && city) {
      setServiceCostForm((prev) => {
        if (prev.cities.length === 0) {
          return { ...prev, cities: [city] };
        }
        if (prev.cities.length === 1 && prev.cities[0] !== city) {
          return { ...prev, cities: [city] };
        }
        return prev;
      });
    }
    if (city) {
      setAuditFilterCity((prev) => (prev === city ? prev : city));
    }
  }, [
    selectedMetrologistCity,
    selectedMetrologistId,
    editingBonusExtraId,
    editingGsmId,
    editingRevenueHandoverId,
    editingExpenseId,
    editingPlanId,
    editingBusinessTripId,
    editingHouseholdId,
    editingServiceCostId,
  ]);

  useEffect(() => {
    setExcelExportEmployeeIds((prev) =>
      prev.filter((id) => filteredMetrologists.some((employee) => employee.id === id)),
    );
  }, [filteredMetrologists]);

  const excelMetrologistMultiOptions = useMemo(
    () =>
      filteredMetrologists.map((employee) => ({
        label: `${employee.fullName}${employee.city ? ` (${employee.city})` : ''}`,
        value: employee.id,
      })),
    [filteredMetrologists],
  );

  const filteredHouseholdRows = useMemo(
    () =>
      householdRows
        .filter((row) => !selectedMetrologistCity || row.city === selectedMetrologistCity)
        .filter((row) => !selectedMetrologistId || row.employeeId === selectedMetrologistId),
    [householdRows, selectedMetrologistCity, selectedMetrologistId],
  );

  const householdOperationsTotalPages = Math.max(
    1,
    Math.ceil(filteredHouseholdRows.length / householdOperationsPageSize),
  );
  const safeHouseholdOperationsPage = Math.min(householdOperationsPage, householdOperationsTotalPages);
  const pagedHouseholdRows = useMemo(() => {
    const start = (safeHouseholdOperationsPage - 1) * householdOperationsPageSize;
    return filteredHouseholdRows.slice(start, start + householdOperationsPageSize);
  }, [filteredHouseholdRows, safeHouseholdOperationsPage, householdOperationsPageSize]);

  useEffect(() => {
    if (householdOperationsPage > householdOperationsTotalPages) {
      setHouseholdOperationsPage(householdOperationsTotalPages);
    }
  }, [householdOperationsPage, householdOperationsTotalPages]);

  const openCreateForm = () => {
    setEditingServiceId(null);
    setServiceForm(initialForm);
    setIsFormOpen(true);
  };

  const openEditForm = (service: SalaryService) => {
    setEditingServiceId(service.id);
    setServiceForm({
      name: service.name,
      description: service.description || '',
      isActive: service.isActive,
      // В режиме редактирования показываем все вариации, чтобы можно было
      // деактивировать/активировать их без "исчезновения" из формы.
      variants: service.variants.map((variant) => ({
        id: variant.id,
        name: variant.name,
        rateType: variant.rateType,
        fixedPrice: variant.fixedPrice !== null && variant.fixedPrice !== undefined ? String(variant.fixedPrice) : '',
        percentage: variant.percentage !== null && variant.percentage !== undefined ? String(variant.percentage) : '',
        bitrixProductId: variant.bitrixProductId ? String(variant.bitrixProductId) : '',
        isActive: variant.isActive,
        monthRates: (variant.monthRates || []).map((mr) => ({
          year: String(mr.year),
          month: String(mr.month),
          rateType: mr.rateType,
          fixedPrice: mr.fixedPrice !== null && mr.fixedPrice !== undefined ? String(mr.fixedPrice) : '',
          percentage: mr.percentage !== null && mr.percentage !== undefined ? String(mr.percentage) : '',
          isActive: mr.isActive ?? true,
        })),
      })),
    });
    setIsFormOpen(true);
  };

  const handleAddVariant = () => {
    setServiceForm((prev) => ({
      ...prev,
      variants: [
        ...prev.variants,
        {
          name: '',
          rateType: 'fixed',
          fixedPrice: '',
          percentage: '',
          bitrixProductId: '',
          isActive: true,
          monthRates: [],
        },
      ],
    }));
  };

  const handleVariantChange = (
    index: number,
    field: keyof VariantFormItem,
    value: string | number | boolean,
  ) => {
    setServiceForm((prev) => ({
      ...prev,
      variants: prev.variants.map((variant, variantIndex) => {
        if (variantIndex !== index) {
          return variant;
        }
        return {
          ...variant,
          [field]: value,
        };
      }),
    }));
  };

  const handleRateTypeChange = (index: number, rateType: 'fixed' | 'percentage') => {
    setServiceForm((prev) => ({
      ...prev,
      variants: prev.variants.map((variant, variantIndex) => {
        if (variantIndex !== index) {
          return variant;
        }
        return {
          ...variant,
          rateType,
          fixedPrice: rateType === 'fixed' ? variant.fixedPrice : '',
          percentage: rateType === 'percentage' ? variant.percentage : '',
        };
      }),
    }));
  };

  const handleRemoveVariant = (index: number) => {
    setServiceForm((prev) => ({
      ...prev,
      variants: prev.variants.filter((_, variantIndex) => variantIndex !== index),
    }));
  };

  const handleAddMonthRate = (variantIndex: number) => {
    const now = new Date();
    setServiceForm((prev) => ({
      ...prev,
      variants: prev.variants.map((variant, idx) =>
        idx !== variantIndex
          ? variant
          : {
              ...variant,
              monthRates: [
                ...variant.monthRates,
                {
                  year: String(now.getFullYear()),
                  month: String(now.getMonth() + 1),
                  rateType: 'fixed',
                  fixedPrice: '',
                  percentage: '',
                  isActive: true,
                },
              ],
            },
      ),
    }));
  };

  const handleMonthRateChange = (
    variantIndex: number,
    monthRateIndex: number,
    field: keyof VariantMonthRateFormItem,
    value: string | boolean,
  ) => {
    setServiceForm((prev) => ({
      ...prev,
      variants: prev.variants.map((variant, idx) =>
        idx !== variantIndex
          ? variant
          : {
              ...variant,
              monthRates: variant.monthRates.map((mr, mrIdx) =>
                mrIdx !== monthRateIndex ? mr : { ...mr, [field]: value },
              ),
            },
      ),
    }));
  };

  const handleRemoveMonthRate = (variantIndex: number, monthRateIndex: number) => {
    setServiceForm((prev) => ({
      ...prev,
      variants: prev.variants.map((variant, idx) =>
        idx !== variantIndex
          ? variant
          : {
              ...variant,
              monthRates: variant.monthRates.filter((_, mrIdx) => mrIdx !== monthRateIndex),
            },
      ),
    }));
  };

  const handleSaveService = async () => {
    if (!serviceForm.name.trim()) {
      alert('Укажите название услуги');
      return;
    }

    const hasInvalidVariant = serviceForm.variants.some(
      (variant) => {
        if (!variant.name.trim()) {
          return true;
        }
        if (variant.rateType === 'fixed') {
          return variant.fixedPrice === '' || Number(variant.fixedPrice) < 0;
        }
        return (
          variant.percentage === '' ||
          Number(variant.percentage) <= 0 ||
          Number(variant.percentage) > 100
        );
      },
    );
    const hasInvalidBitrixId = serviceForm.variants.some((variant) => {
      if (!variant.bitrixProductId.trim()) {
        return false;
      }
      const parsedId = Number(variant.bitrixProductId);
      return !Number.isInteger(parsedId) || parsedId <= 0;
    });
    const hasInvalidMonthRates = serviceForm.variants.some((variant) =>
      variant.monthRates.some((mr) => {
        const year = Number(mr.year);
        const month = Number(mr.month);
        if (!Number.isInteger(year) || year < 2020) return true;
        if (!Number.isInteger(month) || month < 1 || month > 12) return true;
        if (mr.rateType === 'fixed') {
          return mr.fixedPrice === '' || Number(mr.fixedPrice) < 0;
        }
        return mr.percentage === '' || Number(mr.percentage) <= 0 || Number(mr.percentage) > 100;
      }),
    );

    if (hasInvalidVariant) {
      alert(
        'Проверьте вариации: имя обязательно, для fixed нужна сумма >= 0, для percentage значение > 0 и <= 100',
      );
      return;
    }
    if (hasInvalidBitrixId) {
      alert('Bitrix Product ID должен быть целым числом больше 0');
      return;
    }
    if (hasInvalidMonthRates) {
      alert('Проверьте месячные ставки: год >= 2020, месяц 1..12, значения fixed/percentage корректны');
      return;
    }

    const payload = {
      name: serviceForm.name.trim(),
      description: serviceForm.description.trim() || undefined,
      isActive: serviceForm.isActive,
      variants: serviceForm.variants.map((variant): SalaryServiceVariantPayload => ({
        id: variant.id,
        name: variant.name.trim(),
        rateType: variant.rateType,
        fixedPrice: variant.rateType === 'fixed' ? Number(variant.fixedPrice) : null,
        percentage: variant.rateType === 'percentage' ? Number(variant.percentage) : null,
        bitrixProductId: variant.bitrixProductId.trim() ? Number(variant.bitrixProductId) : null,
        isActive: variant.isActive,
        monthRates: variant.monthRates.map((mr) => ({
          year: Number(mr.year),
          month: Number(mr.month),
          rateType: mr.rateType,
          fixedPrice: mr.rateType === 'fixed' ? Number(mr.fixedPrice) : null,
          percentage: mr.rateType === 'percentage' ? Number(mr.percentage) : null,
          isActive: mr.isActive,
        })),
      })),
    };

    try {
      setSaving(true);
      if (editingServiceId) {
        await reportApi.updateSalaryService(editingServiceId, payload);
      } else {
        await reportApi.createSalaryService(payload);
      }
      setIsFormOpen(false);
      setServiceForm(initialForm);
      setEditingServiceId(null);
      await loadSalaryServices();
    } catch (error) {
      console.error('Error saving salary service:', error);
      alert('Не удалось сохранить услугу');
    } finally {
      setSaving(false);
    }
  };

  const handleArchiveService = async (id: string) => {
    if (!confirm('Архивировать услугу и все ее вариации?')) {
      return;
    }

    try {
      await reportApi.archiveSalaryService(id);
      await loadSalaryServices();
    } catch (error) {
      console.error('Error archiving salary service:', error);
      alert('Не удалось архивировать услугу');
    }
  };

  const openCreateEmployeeForm = () => {
    setEditingEmployeeId(null);
    setEmployeeForm(initialEmployeeForm);
    setIsEmployeeFormOpen(true);
  };

  const openEditEmployeeForm = (employee: Employee) => {
    const inactiveFromDate = employee.inactiveFrom ? new Date(employee.inactiveFrom) : null;
    setEditingEmployeeId(employee.id);
    setEmployeeForm({
      fullName: employee.fullName,
      city: employee.city || '',
      role: employee.role,
      bitrixUserId: employee.bitrixUserId ? String(employee.bitrixUserId) : '',
      isActive: employee.isActive,
      inactiveFromMonth:
        inactiveFromDate && !Number.isNaN(inactiveFromDate.getTime())
          ? String(inactiveFromDate.getMonth() + 1).padStart(2, '0')
          : '',
      inactiveFromYear:
        inactiveFromDate && !Number.isNaN(inactiveFromDate.getTime()) ? String(inactiveFromDate.getFullYear()) : '',
    });
    setIsEmployeeFormOpen(true);
  };

  const handleSaveEmployee = async () => {
    if (!employeeForm.fullName.trim()) {
      alert('Укажите ФИО сотрудника');
      return;
    }

    if (employeeForm.bitrixUserId && Number(employeeForm.bitrixUserId) <= 0) {
      alert('Bitrix User ID должен быть больше 0');
      return;
    }

    const hasInactiveMonth = employeeForm.inactiveFromMonth.trim().length > 0;
    const hasInactiveYear = employeeForm.inactiveFromYear.trim().length > 0;
    if (hasInactiveMonth !== hasInactiveYear) {
      alert('Для даты неактивности укажите и месяц, и год');
      return;
    }

    let inactiveFromMonth: number | undefined;
    let inactiveFromYear: number | undefined;

    if (hasInactiveMonth && hasInactiveYear) {
      inactiveFromMonth = Number(employeeForm.inactiveFromMonth);
      inactiveFromYear = Number(employeeForm.inactiveFromYear);
      if (!Number.isInteger(inactiveFromMonth) || inactiveFromMonth < 1 || inactiveFromMonth > 12) {
        alert('Месяц неактивности должен быть от 1 до 12');
        return;
      }
      if (!Number.isInteger(inactiveFromYear) || inactiveFromYear < 2000) {
        alert('Год неактивности указан некорректно');
        return;
      }
    }

    const payload = {
      fullName: employeeForm.fullName.trim(),
      city: employeeForm.city || undefined,
      role: employeeForm.role,
      bitrixUserId: employeeForm.bitrixUserId ? Number(employeeForm.bitrixUserId) : undefined,
      isActive: employeeForm.isActive,
      inactiveFromMonth,
      inactiveFromYear,
    };

    try {
      setSaving(true);
      if (editingEmployeeId) {
        await reportApi.updateEmployee(editingEmployeeId, payload);
      } else {
        await reportApi.createEmployee(payload);
      }
      setIsEmployeeFormOpen(false);
      setEditingEmployeeId(null);
      setEmployeeForm(initialEmployeeForm);
      await loadEmployees();
    } catch (error) {
      console.error('Error saving employee:', error);
      alert('Не удалось сохранить сотрудника');
    } finally {
      setSaving(false);
    }
  };

  const handleArchiveEmployee = async (id: string) => {
    if (!confirm('Архивировать сотрудника?')) {
      return;
    }

    try {
      await reportApi.archiveEmployee(id);
      await loadEmployees();
    } catch (error) {
      console.error('Error archiving employee:', error);
      alert('Не удалось архивировать сотрудника');
    }
  };

  const handleFetchMetrologistWork = async (page: number = 1) => {
    if (!selectedMetrologistCity) {
      alert('Выберите город');
      return;
    }

    if (!selectedMetrologistId) {
      alert('Выберите метролога');
      return;
    }

    if (!periodFrom || !periodTo) {
      alert('Укажите период отчета');
      return;
    }

    if (periodFrom > periodTo) {
      alert('Дата начала не может быть больше даты конца');
      return;
    }

    const fromDate = new Date(periodFrom);
    const toDate = new Date(periodTo);
    const maxRangeMs = 31 * 24 * 60 * 60 * 1000;
    if (toDate.getTime() - fromDate.getTime() > maxRangeMs) {
      alert('Максимальный период отчета: 31 день');
      return;
    }

    const loadPlanAndExpensesForCity = async (city: string, from: string, to: string) => {
      if (!city) {
        setMetrologistPlanTotal(0);
        setMetrologistExpensesTotal(0);
        return;
      }
      try {
        const [plans, expenses] = await Promise.all([reportApi.listPlans({ city }), reportApi.listExpenses({ city })]);
        const fromD = new Date(from);
        const toD = new Date(to);
        const fromKey = fromD.getFullYear() * 100 + (fromD.getMonth() + 1);
        const toKey = toD.getFullYear() * 100 + (toD.getMonth() + 1);

        const planTotal = plans
          .filter((row) => {
            if ((row.city || '').trim() !== city.trim()) return false;
            const key = Number(row.year) * 100 + Number(row.month);
            return key >= fromKey && key <= toKey;
          })
          .reduce((sum, row) => sum + Number(row.amount || 0), 0);

        const expensesTotal = expenses
          .filter((row) => {
            if ((row.city || '').trim() !== city.trim()) return false;
            const date = new Date(row.date);
            if (Number.isNaN(date.getTime())) return false;
            return date.getTime() >= fromD.getTime() && date.getTime() <= toD.getTime();
          })
          .reduce((sum, row) => sum + Number(row.amount || 0), 0);

        setMetrologistPlanTotal(Number(planTotal.toFixed(2)));
        setMetrologistExpensesTotal(Number(expensesTotal.toFixed(2)));
      } catch (error) {
        console.error('Error loading plan/expenses for metrologist report:', error);
        setMetrologistPlanTotal(0);
        setMetrologistExpensesTotal(0);
      }
    };

    try {
      setMetrologistLoading(true);
      setIsCityAggregateView(false);
      setCityAggregateAllDeals([]);
      setMetrologistFinanceRows([]);
      setCityCashSummary(null);
      const [data, serviceCosts] = await Promise.all([
        reportApi.fetchMetrologistWork(
          selectedMetrologistId,
          periodFrom,
          periodTo,
          page,
          metrologistPageSize,
          selectedMetrologistCity || undefined,
        ),
        reportApi.listServiceCosts({ city: selectedMetrologistCity }),
      ]);
      setServiceCostRows(serviceCosts);
      setMetrologistWork(data);
      setMetrologistPage(data.pagination?.page ?? page);
      const [handoverRowsData, gsmRowsData, bonusRowsData] = await Promise.all([
        reportApi.listRevenueHandovers({
          city: selectedMetrologistCity,
          employeeId: selectedMetrologistId,
          from: periodFrom,
          to: periodTo,
        }),
        reportApi.listGsmEntries({
          city: selectedMetrologistCity,
          employeeId: selectedMetrologistId,
          from: periodFrom,
          to: periodTo,
        }),
        reportApi.listBonusExtras({
          city: selectedMetrologistCity,
          employeeId: selectedMetrologistId,
          from: periodFrom,
          to: periodTo,
        }),
      ]);
      const handoverTotal = handoverRowsData.reduce((sum, row) => sum + Number(row.amount || 0), 0);
      const gsmTotal = gsmRowsData.reduce((sum, row) => sum + Number(row.amount || 0), 0);
      const bonusExtraTotal = bonusRowsData.reduce((sum, row) => sum + Number(row.amount || 0), 0);
      setMetrologistFinanceRows([
        buildFinanceRow({
          employeeId: data.employee.id,
          fullName: data.employee.fullName,
          summary: data.summary,
          salaryAccrued: Number(data.totalReward || 0),
          handoverTotal,
          gsmTotal,
          bonusExtraTotal,
        }),
      ]);
      await loadPlanAndExpensesForCity(selectedMetrologistCity, periodFrom, periodTo);
    } catch (error) {
      console.error('Error fetching metrologist work:', error);
      const host = window.location.hostname;
      const isLocalhost = host === 'localhost' || host === '127.0.0.1' || host === '::1';
      if (isLocalhost) {
        const selectedEmployee = metrologists.find((employee) => employee.id === selectedMetrologistId);
        const emptySummary = {
          water: {
            nonBank: {
              verification: { amount: 0, quantity: 0, accrual: 0 },
              diagnosticsWater: { amount: 0, quantity: 0, accrual: 0 },
              waterCounters: { amount: 0, quantity: 0, accrual: 0 },
              replacement: { amount: 0, quantity: 0, accrual: 0 },
              sealing: { amount: 0, quantity: 0, accrual: 0 },
              accrualTotal: 0,
              revenueTotal: 0,
            },
            bankAccount: {
              verification: { amount: 0, quantity: 0, accrual: 0 },
              diagnosticsWater: { amount: 0, quantity: 0, accrual: 0 },
              waterCounters: { amount: 0, quantity: 0, accrual: 0 },
              replacement: { amount: 0, quantity: 0, accrual: 0 },
              sealing: { amount: 0, quantity: 0, accrual: 0 },
              accrualTotal: 0,
              revenueTotal: 0,
            },
          },
          heat: {
            nonBank: {
              verification: { quantity: 0, amount: 0, accrual: 0 },
              mounting: { quantity: 0, amount: 0, accrual: 0 },
              sale: { quantity: 0, amount: 0, accrual: 0 },
              batteryReplacement: { quantity: 0, amount: 0, accrual: 0 },
              accrualTotal: 0,
              revenueTotal: 0,
            },
            bankAccount: {
              verification: { quantity: 0, amount: 0, accrual: 0 },
              mounting: { quantity: 0, amount: 0, accrual: 0 },
              sale: { quantity: 0, amount: 0, accrual: 0 },
              batteryReplacement: { quantity: 0, amount: 0, accrual: 0 },
              accrualTotal: 0,
              revenueTotal: 0,
            },
          },
          gas: {
            nonBank: {
              verification: { quantity: 0 },
              accrualTotal: 0,
              revenueTotal: 0,
            },
            bankAccount: {
              verification: { quantity: 0 },
              accrualTotal: 0,
              revenueTotal: 0,
            },
          },
        };
        setMetrologistWork({
          employee: {
            id: selectedMetrologistId,
            fullName: selectedEmployee?.fullName || 'Локальный режим',
            city: selectedMetrologistCity || selectedEmployee?.city || undefined,
          },
          period: { from: periodFrom, to: periodTo },
          totalReward: 0,
          summary: emptySummary,
          work: [],
          pagination: {
            page: 1,
            pageSize: metrologistPageSize,
            totalItems: 0,
            totalPages: 1,
            hasNext: false,
            hasPrev: false,
          },
        });
        setMetrologistPage(1);
        setMetrologistFinanceRows([]);
        setCityCashSummary(null);
        await loadPlanAndExpensesForCity(selectedMetrologistCity, periodFrom, periodTo);
        return;
      }
      setMetrologistFinanceRows([]);
      setCityCashSummary(null);
      alert('Не удалось сформировать отчет по метрологу');
    } finally {
      setMetrologistLoading(false);
    }
  };

  const handleFetchMetrologistCityWork = async () => {
    if (!selectedMetrologistCity) {
      alert('Выберите город');
      return;
    }
    if (!periodFrom || !periodTo) {
      alert('Укажите период отчета');
      return;
    }
    if (periodFrom > periodTo) {
      alert('Дата начала не может быть больше даты конца');
      return;
    }
    const fromDate = new Date(periodFrom);
    const toDate = new Date(periodTo);
    const maxRangeMs = 31 * 24 * 60 * 60 * 1000;
    if (toDate.getTime() - fromDate.getTime() > maxRangeMs) {
      alert('Максимальный период отчета: 31 день');
      return;
    }

    const loadPlanAndExpensesForCity = async (city: string, from: string, to: string) => {
      if (!city) {
        setMetrologistPlanTotal(0);
        setMetrologistExpensesTotal(0);
        return;
      }
      try {
        const [plans, expenses] = await Promise.all([reportApi.listPlans({ city }), reportApi.listExpenses({ city })]);
        const fromD = new Date(from);
        const toD = new Date(to);
        const fromKey = fromD.getFullYear() * 100 + (fromD.getMonth() + 1);
        const toKey = toD.getFullYear() * 100 + (toD.getMonth() + 1);

        const planTotal = plans
          .filter((row) => {
            if ((row.city || '').trim() !== city.trim()) return false;
            const key = Number(row.year) * 100 + Number(row.month);
            return key >= fromKey && key <= toKey;
          })
          .reduce((sum, row) => sum + Number(row.amount || 0), 0);

        const expensesTotal = expenses
          .filter((row) => {
            if ((row.city || '').trim() !== city.trim()) return false;
            const date = new Date(row.date);
            if (Number.isNaN(date.getTime())) return false;
            return date.getTime() >= fromD.getTime() && date.getTime() <= toD.getTime();
          })
          .reduce((sum, row) => sum + Number(row.amount || 0), 0);

        setMetrologistPlanTotal(Number(planTotal.toFixed(2)));
        setMetrologistExpensesTotal(Number(expensesTotal.toFixed(2)));
      } catch (error) {
        console.error('Error loading city plan/expenses for metrologist report:', error);
        setMetrologistPlanTotal(0);
        setMetrologistExpensesTotal(0);
      }
    };

    try {
      setMetrologistLoading(true);
      setMetrologistFinanceRows([]);
      setCityCashSummary(null);
      const [report, serviceCosts, bonusRowsData] = await Promise.all([
        reportApi.fetchMetrologistCityWork(
          selectedMetrologistCity,
          periodFrom,
          periodTo,
          1,
          metrologistPageSize,
        ),
        reportApi.listServiceCosts({ city: selectedMetrologistCity }),
        reportApi.listBonusExtras({ city: selectedMetrologistCity, from: periodFrom, to: periodTo }),
      ]);
      setServiceCostRows(serviceCosts);
      const bonusByEmployee = new Map<string, number>();
      for (const row of bonusRowsData) {
        const key = String(row.employeeId || '').trim();
        if (!key) continue;
        bonusByEmployee.set(key, Number((bonusByEmployee.get(key) || 0) + Number(row.amount || 0)));
      }
      const allDeals = report.cityAggregate?.allDeals || report.work || [];
      const totalItems = allDeals.length;
      const totalPages = Math.max(1, Math.ceil(totalItems / metrologistPageSize));
      const pagedWork = allDeals.slice(0, metrologistPageSize);

      setIsCityAggregateView(true);
      setCityAggregateAllDeals(allDeals);
      setMetrologistWork({
        employee: report.employee,
        period: report.period,
        totalReward: report.totalReward,
        summary: report.summary,
        work: pagedWork,
        pagination: {
          page: 1,
          pageSize: metrologistPageSize,
          totalItems,
          totalPages,
          hasNext: totalPages > 1,
          hasPrev: false,
        },
      });
      setMetrologistPage(1);
      const cityRows = (report.cityAggregate?.employeeRows || []).map((row) => ({
        employeeId: row.employeeId,
        fullName: row.fullName,
        salaryAccrued: Number(
          (Number(row.salaryAccrued || 0) + Number(row.gsm || 0) + Number(bonusByEmployee.get(row.employeeId) || 0)).toFixed(2),
        ),
        unsurrenderedRevenue: Number(row.unsurrenderedRevenue || 0),
        cashRevenue: Number(row.cashRevenue || 0),
        bankRevenue: Number(row.bankRevenue || 0),
        totalRevenue: Number(row.totalRevenue || 0),
        gsm: Number(row.gsm || 0),
      }));
      setMetrologistFinanceRows(cityRows);
      const citySummary = report.cityAggregate?.cityCashSummary;
      setCityCashSummary(
        citySummary
          ? {
              cashInRegister: Number(citySummary.cashInRegister || 0),
              surrenderedRevenueAllTime: Number(citySummary.surrenderedRevenueAllTime || 0),
              leftToHandoverPeriod: Number(citySummary.leftToHandoverPeriod || 0),
            }
          : null,
      );
      await loadPlanAndExpensesForCity(selectedMetrologistCity, periodFrom, periodTo);
    } catch (error: any) {
      console.error('Error fetching city metrologist work:', error);
      setIsCityAggregateView(false);
      setCityAggregateAllDeals([]);
      setMetrologistWork(null);
      setMetrologistPage(1);
      setMetrologistFinanceRows([]);
      setCityCashSummary(null);
      const status = error?.response?.status;
      if (status === 504 || status === 502 || status === 503) {
        alert(
          'Сервер не успел сформировать отчёт за отведённое время (504). Обычно при повторном нажатии он уже открывается быстрее — данные уже посчитаны. Подождите и нажмите ещё раз.',
        );
        return;
      }
      const apiMessage =
        error?.response?.data?.message ||
        error?.message ||
        'Не удалось сформировать отчет по всему городу';
      alert(Array.isArray(apiMessage) ? apiMessage.join(', ') : String(apiMessage));
    } finally {
      setMetrologistLoading(false);
    }
  };

  const handleMetrologistPageChange = async (nextPage: number) => {
    if (metrologistLoading || nextPage < 1) {
      return;
    }
    if (isCityAggregateView) {
      const totalItems = cityAggregateAllDeals.length;
      const totalPages = Math.max(1, Math.ceil(totalItems / metrologistPageSize));
      const safePage = Math.min(nextPage, totalPages);
      const startIndex = (safePage - 1) * metrologistPageSize;
      const pagedWork = cityAggregateAllDeals.slice(startIndex, startIndex + metrologistPageSize);
      setMetrologistPage(safePage);
      setMetrologistWork((prev) =>
        prev
          ? {
              ...prev,
              work: pagedWork,
              pagination: {
                page: safePage,
                pageSize: metrologistPageSize,
                totalItems,
                totalPages,
                hasNext: safePage < totalPages,
                hasPrev: safePage > 1,
              },
            }
          : prev,
      );
      return;
    }
    await handleFetchMetrologistWork(nextPage);
  };

  const handleDownloadMetrologistExcel = async () => {
    if (!periodFrom || !periodTo) {
      alert('Укажите период');
      return;
    }
    if (!selectedMetrologistId) {
      alert('Выберите метролога');
      return;
    }
    try {
      setMetrologistLoading(true);
      const params = { from: periodFrom, to: periodTo, employeeId: selectedMetrologistId };
      const response = await reportApi.downloadMetrologistWorkExcel(params);
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const contentDisposition = response.headers?.['content-disposition'] as string | undefined;
      const fileNameMatch = contentDisposition?.match(/filename=\"?([^\";]+)\"?/i);
      const fallbackName = `report_${selectedMetrologistId}_${periodFrom}-${periodTo}.xlsx`;
      const fileName = fileNameMatch?.[1] || fallbackName;
      const downloadUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error('Error downloading metrologist excel report:', error);
      alert('Не удалось скачать отчет в Excel');
    } finally {
      setMetrologistLoading(false);
    }
  };

  const handleDownloadMetrologistCityExcel = async () => {
    if (!selectedMetrologistCity) {
      alert('Выберите город');
      return;
    }
    if (!periodFrom || !periodTo) {
      alert('Укажите период');
      return;
    }
    try {
      setMetrologistLoading(true);
      const params =
        excelExportEmployeeIds.length >= 2
          ? { from: periodFrom, to: periodTo, employeeIds: excelExportEmployeeIds }
          : excelExportEmployeeIds.length === 1
            ? { from: periodFrom, to: periodTo, employeeId: excelExportEmployeeIds[0] }
            : { from: periodFrom, to: periodTo, exportCity: selectedMetrologistCity };
      const response = await reportApi.downloadMetrologistWorkExcel(params);
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const contentDisposition = response.headers?.['content-disposition'] as string | undefined;
      const fileNameMatch = contentDisposition?.match(/filename=\"?([^\";]+)\"?/i);
      const fallbackName =
        excelExportEmployeeIds.length >= 2
          ? `report_multi_${excelExportEmployeeIds.length}_${periodFrom}-${periodTo}.xlsx`
          : excelExportEmployeeIds.length === 1
            ? `report_${excelExportEmployeeIds[0]}_${periodFrom}-${periodTo}.xlsx`
            : `report_city_${selectedMetrologistCity}_${periodFrom}-${periodTo}.xlsx`;
      const fileName = fileNameMatch?.[1] || fallbackName;
      const downloadUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error('Error downloading city metrologist excel report:', error);
      const details = await extractApiErrorMessage(error);
      alert(details ? `Не удалось скачать отчет по городу в Excel: ${details}` : 'Не удалось скачать отчет по городу в Excel');
    } finally {
      setMetrologistLoading(false);
    }
  };

  const handleDownloadMetrologistPdf = async () => {
    if (!periodFrom || !periodTo) {
      alert('Укажите период');
      return;
    }
    if (!selectedMetrologistId) {
      alert('Выберите метролога');
      return;
    }
    try {
      setMetrologistLoading(true);
      const params = { from: periodFrom, to: periodTo, employeeId: selectedMetrologistId };
      const response = await reportApi.downloadMetrologistWorkPdf(params);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const contentDisposition = response.headers?.['content-disposition'] as string | undefined;
      const fileNameMatch = contentDisposition?.match(/filename=\"?([^\";]+)\"?/i);
      const fallbackName = `report_${selectedMetrologistId}_${periodFrom}-${periodTo}.pdf`;
      const fileName = fileNameMatch?.[1] || fallbackName;
      const downloadUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error('Error downloading metrologist pdf report:', error);
      alert('Не удалось скачать отчет в PDF');
    } finally {
      setMetrologistLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-base-content">Отчеты</h1>
          <p className="text-sm text-base-content/70 mt-2">
            Раздел для формирования и просмотра отчетов.
          </p>
        </div>
      </div>

      <div className="border-b border-base-300">
        <nav className="-mb-px grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-x-2 gap-y-1 px-1">
          <button
            className={`py-2 md:py-3 px-2 border-b-2 font-medium text-xs md:text-sm transition-colors whitespace-nowrap flex items-center justify-center gap-1.5 ${
              activeTab === 'metrologists'
                ? 'border-primary text-primary'
                : 'border-transparent text-base-content/70 hover:text-base-content hover:border-base-300'
            }`}
            onClick={() => setActiveTab('metrologists')}
          >
            <ClipboardDocumentListIcon className="h-4 w-4 shrink-0 md:h-5 md:w-5" aria-hidden />
            <span>Отчет метрологи</span>
          </button>
          <button
            className={`py-2 md:py-3 px-2 border-b-2 font-medium text-xs md:text-sm transition-colors whitespace-nowrap flex items-center justify-center gap-1.5 ${
              activeTab === 'bonusExtras'
                ? 'border-primary text-primary'
                : 'border-transparent text-base-content/70 hover:text-base-content hover:border-base-300'
            }`}
            onClick={() => setActiveTab('bonusExtras')}
          >
            <GiftIcon className="h-4 w-4 shrink-0 md:h-5 md:w-5" aria-hidden />
            <span>Доп начисления</span>
          </button>
          <button
            className={`py-2 md:py-3 px-2 border-b-2 font-medium text-xs md:text-sm transition-colors whitespace-nowrap flex items-center justify-center gap-1.5 ${
              activeTab === 'gsmDev'
                ? 'border-primary text-primary'
                : 'border-transparent text-base-content/70 hover:text-base-content hover:border-base-300'
            }`}
            onClick={() => setActiveTab('gsmDev')}
          >
            <BeakerIcon className="h-4 w-4 shrink-0 md:h-5 md:w-5" aria-hidden />
            <span>ГСМ</span>
          </button>
          <button
            className={`py-2 md:py-3 px-2 border-b-2 font-medium text-xs md:text-sm transition-colors whitespace-nowrap flex items-center justify-center gap-1.5 ${
              activeTab === 'revenueHandovers'
                ? 'border-primary text-primary'
                : 'border-transparent text-base-content/70 hover:text-base-content hover:border-base-300'
            }`}
            onClick={() => setActiveTab('revenueHandovers')}
          >
            <BanknotesIcon className="h-4 w-4 shrink-0 md:h-5 md:w-5" aria-hidden />
            <span>Сдача выручки</span>
          </button>
          <button
            className={`py-2 md:py-3 px-2 border-b-2 font-medium text-xs md:text-sm transition-colors whitespace-nowrap flex items-center justify-center gap-1.5 ${
              activeTab === 'expensesDev'
                ? 'border-primary text-primary'
                : 'border-transparent text-base-content/70 hover:text-base-content hover:border-base-300'
            }`}
            onClick={() => setActiveTab('expensesDev')}
          >
            <ReceiptPercentIcon className="h-4 w-4 shrink-0 md:h-5 md:w-5" aria-hidden />
            <span>Расходы</span>
          </button>
          <button
            className={`py-2 md:py-3 px-2 border-b-2 font-medium text-xs md:text-sm transition-colors whitespace-nowrap flex items-center justify-center gap-1.5 ${
              activeTab === 'plansDev'
                ? 'border-primary text-primary'
                : 'border-transparent text-base-content/70 hover:text-base-content hover:border-base-300'
            }`}
            onClick={() => setActiveTab('plansDev')}
          >
            <CalendarDaysIcon className="h-4 w-4 shrink-0 md:h-5 md:w-5" aria-hidden />
            <span>План</span>
          </button>
          <button
            className={`py-2 md:py-3 px-2 border-b-2 font-medium text-xs md:text-sm transition-colors whitespace-nowrap flex items-center justify-center gap-1.5 ${
              activeTab === 'businessTripsDev'
                ? 'border-primary text-primary'
                : 'border-transparent text-base-content/70 hover:text-base-content hover:border-base-300'
            }`}
            onClick={() => setActiveTab('businessTripsDev')}
          >
            <MapPinIcon className="h-4 w-4 shrink-0 md:h-5 md:w-5" aria-hidden />
            <span>Командировки</span>
          </button>
          <button
            className={`py-2 md:py-3 px-2 border-b-2 font-medium text-xs md:text-sm transition-colors whitespace-nowrap flex items-center justify-center gap-1.5 ${
              activeTab === 'householdDev'
                ? 'border-primary text-primary'
                : 'border-transparent text-base-content/70 hover:text-base-content hover:border-base-300'
            }`}
            onClick={() => setActiveTab('householdDev')}
          >
            <CubeIcon className="h-4 w-4 shrink-0 md:h-5 md:w-5" aria-hidden />
            <span>Хоз блок</span>
          </button>
          <button
            className={`py-2 md:py-3 px-2 border-b-2 font-medium text-xs md:text-sm transition-colors whitespace-nowrap flex items-center justify-center gap-1.5 ${
              activeTab === 'serviceCostsDev'
                ? 'border-primary text-primary'
                : 'border-transparent text-base-content/70 hover:text-base-content hover:border-base-300'
            }`}
            onClick={() => setActiveTab('serviceCostsDev')}
          >
            <WrenchScrewdriverIcon className="h-4 w-4 shrink-0 md:h-5 md:w-5" aria-hidden />
            <span>Себестоимость</span>
          </button>
          {isAdminUser && (
            <button
              className={`py-2 md:py-3 px-2 border-b-2 font-medium text-xs md:text-sm transition-colors whitespace-nowrap flex items-center justify-center gap-1.5 ${
                activeTab === 'auditConsole'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-base-content/70 hover:text-base-content hover:border-base-300'
              }`}
              onClick={() => setActiveTab('auditConsole')}
            >
              <CommandLineIcon className="h-4 w-4 shrink-0 md:h-5 md:w-5" aria-hidden />
              <span>Консоль</span>
            </button>
          )}
          <button
            className={`py-2 md:py-3 px-2 border-b-2 font-medium text-xs md:text-sm transition-colors whitespace-nowrap flex items-center justify-center gap-1.5 ${
              activeTab === 'employees'
                ? 'border-primary text-primary'
                : 'border-transparent text-base-content/70 hover:text-base-content hover:border-base-300'
            }`}
            onClick={() => setActiveTab('employees')}
          >
            <UserGroupIcon className="h-4 w-4 shrink-0 md:h-5 md:w-5" aria-hidden />
            <span>Сотрудники</span>
          </button>
          {!isSalaryUser && (
            <button
              className={`py-2 md:py-3 px-2 border-b-2 font-medium text-xs md:text-sm transition-colors whitespace-nowrap flex items-center justify-center gap-1.5 ${
                activeTab === 'products'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-base-content/70 hover:text-base-content hover:border-base-300'
              }`}
              onClick={() => setActiveTab('products')}
            >
              <CubeIcon className="h-4 w-4 shrink-0 md:h-5 md:w-5" aria-hidden />
              <span>Услуги</span>
            </button>
          )}
        </nav>
      </div>

      {activeTab === 'metrologists' && (
        <div className="space-y-6">
          <div>
            <h2 className="text-xl md:text-2xl font-bold text-base-content">Отчет метрологи</h2>
            <p className="text-sm md:text-base font-medium text-base-content/70 mt-2">
              Сделки и начисления по выбранному метрологу за период
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-5 gap-3 items-end">
            <label className="form-control">
              <span className="label-text">Город</span>
              <Dropdown
                value={selectedMetrologistCity}
                onChange={(e) => applySharedCity(e.value || '')}
                options={metrologistCityOptions}
                optionLabel="label"
                optionValue="value"
                className="w-full"
                placeholder="Выберите город"
                showClear
                disabled={metrologistLoading}
              />
            </label>

            <label className="form-control md:col-span-2">
              <span className="label-text">Сотрудник (необязательно)</span>
              <Dropdown
                value={selectedMetrologistId}
                onChange={(e) => applySharedEmployee(e.value || '')}
                options={filteredMetrologists.map((employee) => ({
                  label: `${employee.fullName}${employee.city ? ` (${employee.city})` : ''}`,
                  value: employee.id,
                }))}
                optionLabel="label"
                optionValue="value"
                className="w-full"
                placeholder={selectedMetrologistCity ? 'Выберите метролога' : 'Сначала выберите город'}
                showClear
                disabled={metrologistLoading || !selectedMetrologistCity}
              />
            </label>

            <label className="form-control">
              <span className="label-text">Дата начала</span>
              <input
                type="date"
                className="input input-bordered"
                value={periodFrom}
                onChange={(e) => {
                  setPeriodFrom(e.target.value);
                  setMetrologistPage(1);
                  setMetrologistWork(null);
                }}
              />
            </label>

            <label className="form-control">
              <span className="label-text">Дата конца</span>
              <input
                type="date"
                className="input input-bordered"
                value={periodTo}
                onChange={(e) => {
                  setPeriodTo(e.target.value);
                  setMetrologistPage(1);
                  setMetrologistWork(null);
                }}
              />
            </label>

            <div className="form-control w-full min-w-0 md:col-span-5 md:col-start-1">
              <span className="label-text">
                Excel по всему городу: необязательно отметьте нескольких метрологов. Если пусто — выгружаются все
                метрологи города.
              </span>
              <div className="w-full min-w-0 [&_.p-multiselect]:w-full [&_.p-multiselect]:max-w-none">
                <MultiSelect
                  value={excelExportEmployeeIds}
                  options={excelMetrologistMultiOptions}
                  onChange={(e) => setExcelExportEmployeeIds(e.value || [])}
                  optionLabel="label"
                  optionValue="value"
                  placeholder={
                    selectedMetrologistCity
                      ? 'Необязательно: выберите нескольких метрологов'
                      : 'Сначала выберите город'
                  }
                  display="chip"
                  filter
                  showClear
                  className="w-full"
                  disabled={
                    metrologistLoading || !selectedMetrologistCity || excelMetrologistMultiOptions.length === 0
                  }
                />
              </div>
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            <button
              className="btn btn-primary"
              onClick={() => handleFetchMetrologistWork(1)}
              disabled={metrologistLoading || !selectedMetrologistCity || !selectedMetrologistId}
            >
              {metrologistLoading ? 'Загрузка...' : 'Показать'}
            </button>
            <button
              className="btn btn-secondary"
              onClick={handleFetchMetrologistCityWork}
              disabled={metrologistLoading || !selectedMetrologistCity || !periodFrom || !periodTo}
            >
              Показать по всему городу
            </button>
            <button
              className="btn btn-outline"
              onClick={handleDownloadMetrologistExcel}
              disabled={
                metrologistLoading ||
                !periodFrom ||
                !periodTo ||
                !selectedMetrologistId
              }
            >
              Выгрузить в Excel
            </button>
            <button
              className="btn btn-outline"
              onClick={handleDownloadMetrologistCityExcel}
              disabled={metrologistLoading || !selectedMetrologistCity || !periodFrom || !periodTo}
            >
              Excel по всему городу
            </button>
            <button
              className="btn btn-outline"
              onClick={handleDownloadMetrologistPdf}
              disabled={
                metrologistLoading ||
                !periodFrom ||
                !periodTo ||
                !selectedMetrologistId
              }
            >
              Выгрузить в PDF
            </button>
          </div>

          {metrologistLoading && (
            <div className="flex justify-center py-8">
              <span className="loading loading-spinner loading-md text-primary"></span>
            </div>
          )}

          {!metrologistLoading && metrologistWork && (
            <div className="space-y-4">
              <div className="rounded-lg border border-base-300 p-4">
                <div className="text-sm text-base-content/80">
                  <span className="font-semibold text-base-content">{metrologistWork.employee.fullName}</span>
                  {metrologistWork.employee.city ? `, ${metrologistWork.employee.city}` : ''}
                </div>
                <div className="text-sm text-base-content/70 mt-1">
                  Период: {formatDateRu(metrologistWork.period.from)} - {formatDateRu(metrologistWork.period.to)}
                </div>
                <div className="text-lg font-semibold mt-2">
                  Итого начисление: {displayedAccruedTotal.toLocaleString('ru-RU')} руб.
                </div>
                <div className="mt-3 text-sm">
                  {(() => {
                    const marginClass = (value: number) =>
                      value > 0
                        ? 'text-success font-semibold'
                        : value < 0
                          ? 'text-error font-semibold'
                          : 'text-base-content/80';

                    const waterRows = [...METROLOGIST_WATER_ROW_KEYS];
                    const heatRows = [...METROLOGIST_HEAT_ROW_KEYS];
                    const gasRows = [...METROLOGIST_GAS_ROW_KEYS];

                    const reportCity = String(metrologistWork.employee.city || selectedMetrologistCity || '').trim();
                    const periodEnd = String(metrologistWork.period.to || '');
                    const periodYear = Number(periodEnd.slice(0, 4)) || new Date().getFullYear();
                    const periodMonth = Number(periodEnd.slice(5, 7)) || new Date().getMonth() + 1;
                    const cityCostRows = serviceCostRows
                      .filter((row) => String(row.city || '').trim() === reportCity)
                      .sort((a, b) => (b.year - a.year) || (b.month - a.month));
                    const currentYm = periodYear * 100 + periodMonth;
                    const matchedServiceCost =
                      cityCostRows.find((row) => row.year === periodYear && row.month === periodMonth) ||
                      cityCostRows.find((row) => row.year * 100 + row.month <= currentYm) ||
                      cityCostRows[0];

                    const getWaterUnitCost = (rowKey: (typeof METROLOGIST_WATER_ROW_KEYS)[number]) => {
                      if (!matchedServiceCost) return 0;
                      if (rowKey === 'verification') return Number(matchedServiceCost.waterVerification || 0);
                      if (rowKey === 'diagnosticsWater') return Number(matchedServiceCost.waterDiagnostics || 0);
                      if (rowKey === 'waterCounters') return Number(matchedServiceCost.waterSale || 0);
                      if (rowKey === 'replacement') return Number(matchedServiceCost.waterReplacement || 0);
                      return Number(matchedServiceCost.waterSealing || 0);
                    };
                    const getHeatUnitCost = (rowKey: (typeof METROLOGIST_HEAT_ROW_KEYS)[number]) => {
                      if (!matchedServiceCost) return 0;
                      if (rowKey === 'verification') return Number(matchedServiceCost.heatVerification || 0);
                      if (rowKey === 'mounting') return Number(matchedServiceCost.heatMounting || 0);
                      if (rowKey === 'sale') return Number(matchedServiceCost.heatSale || 0);
                      return Number(matchedServiceCost.heatBatteryReplacement || 0);
                    };
                    const getGasUnitCost = () => Number(matchedServiceCost?.gasVerification || 0);

                    const waterCostTotal = waterRows.reduce((acc, rowKey) => {
                      const qtyValue =
                        waterQtyForRow(metrologistWork.summary.water.nonBank, rowKey) +
                        waterQtyForRow(metrologistWork.summary.water.bankAccount, rowKey);
                      return acc + qtyValue * getWaterUnitCost(rowKey);
                    }, 0);
                    const heatCostTotal = heatRows.reduce((acc, rowKey) => {
                      const qtyValue =
                        heatQtyForRow(metrologistWork.summary.heat.nonBank, rowKey) +
                        heatQtyForRow(metrologistWork.summary.heat.bankAccount, rowKey);
                      return acc + qtyValue * getHeatUnitCost(rowKey);
                    }, 0);
                    const gasCostTotal = gasRows.reduce((acc, rowKey) => {
                      const qtyValue =
                        metrologistWork.summary.gas.nonBank.verification.quantity +
                        metrologistWork.summary.gas.bankAccount.verification.quantity;
                      if (rowKey !== 'verification') return acc;
                      return acc + qtyValue * getGasUnitCost();
                    }, 0);

                    const sectionTotals = {
                      water: {
                        sum:
                          metrologistWork.summary.water.nonBank.revenueTotal +
                          metrologistWork.summary.water.bankAccount.revenueTotal,
                        cash: metrologistWork.summary.water.nonBank.revenueTotal,
                        bank: metrologistWork.summary.water.bankAccount.revenueTotal,
                        qty: waterRows.reduce(
                          (acc, rowKey) =>
                            acc +
                            waterQtyForRow(metrologistWork.summary.water.nonBank, rowKey) +
                            waterQtyForRow(metrologistWork.summary.water.bankAccount, rowKey),
                          0,
                        ),
                        cost: waterCostTotal,
                        salary:
                          metrologistWork.summary.water.nonBank.accrualTotal +
                          metrologistWork.summary.water.bankAccount.accrualTotal,
                      },
                      heat: {
                        sum:
                          metrologistWork.summary.heat.nonBank.revenueTotal +
                          metrologistWork.summary.heat.bankAccount.revenueTotal,
                        cash: metrologistWork.summary.heat.nonBank.revenueTotal,
                        bank: metrologistWork.summary.heat.bankAccount.revenueTotal,
                        qty: heatRows.reduce(
                          (acc, rowKey) =>
                            acc +
                            heatQtyForRow(metrologistWork.summary.heat.nonBank, rowKey) +
                            heatQtyForRow(metrologistWork.summary.heat.bankAccount, rowKey),
                          0,
                        ),
                        cost: heatCostTotal,
                        salary:
                          metrologistWork.summary.heat.nonBank.accrualTotal +
                          metrologistWork.summary.heat.bankAccount.accrualTotal,
                      },
                      gas: {
                        sum:
                          metrologistWork.summary.gas.nonBank.revenueTotal +
                          metrologistWork.summary.gas.bankAccount.revenueTotal,
                        cash: metrologistWork.summary.gas.nonBank.revenueTotal,
                        bank: metrologistWork.summary.gas.bankAccount.revenueTotal,
                        qty:
                          metrologistWork.summary.gas.nonBank.verification.quantity +
                          metrologistWork.summary.gas.bankAccount.verification.quantity,
                        cost: gasCostTotal,
                        salary:
                          metrologistWork.summary.gas.nonBank.accrualTotal +
                          metrologistWork.summary.gas.bankAccount.accrualTotal,
                      },
                    };

                    const overall = {
                      sum: sectionTotals.water.sum + sectionTotals.heat.sum + sectionTotals.gas.sum,
                      cash: sectionTotals.water.cash + sectionTotals.heat.cash + sectionTotals.gas.cash,
                      bank: sectionTotals.water.bank + sectionTotals.heat.bank + sectionTotals.gas.bank,
                      qty: sectionTotals.water.qty + sectionTotals.heat.qty + sectionTotals.gas.qty,
                      cost: sectionTotals.water.cost + sectionTotals.heat.cost + sectionTotals.gas.cost,
                      salary: sectionTotals.water.salary + sectionTotals.heat.salary + sectionTotals.gas.salary,
                    };
                    const overallMargin = overall.sum - overall.cost - overall.salary;
                    const premiumBase = overallMargin - metrologistExpensesTotal;
                    const premiumBaseClass =
                      premiumBase > 0
                        ? 'text-white font-extrabold'
                        : premiumBase < 0
                          ? 'text-red-100 font-extrabold'
                          : 'text-white/90 font-semibold';
                    const normalizeTextForExpenses = (value: string) =>
                      String(value || '')
                        .toLowerCase()
                        .replace(/ё/g, 'е')
                        .replace(/\s+/g, '');
                    const isHeatDealWithMountingTitle = (rawTitle: string): boolean => {
                      const title = normalizeTextForExpenses(rawTitle);
                      const isHeat = title.includes('тепло') || title.includes('теплосчет');
                      if (!isHeat) return false;
                      const hasMounting =
                        title.includes('демонтаж/монтаж') || title.includes('демонтаж') || title.includes('монтаж');
                      const withoutMounting =
                        title.includes('бездемонтажа/монтажа') ||
                        title.includes('бездемонтажа') ||
                        title.includes('безмонтажа');
                      return hasMounting && !withoutMounting;
                    };
                    const dedupeEligibleDeals = (deals: Array<{ dealId?: string; title: string; closedAt?: string }>) => {
                      const seen = new Set<string>();
                      const result: Array<{ dealId: string; title: string; closedAt?: string }> = [];
                      for (const deal of deals) {
                        const title = String(deal.title || '');
                        if (isHeatDealWithMountingTitle(title)) continue;
                        const id = String(deal.dealId || '').trim();
                        const key = id || `${title}|${String(deal.closedAt || '')}`;
                        if (seen.has(key)) continue;
                        seen.add(key);
                        result.push({ dealId: id || key, title, closedAt: deal.closedAt });
                      }
                      return result;
                    };
                    const allDealsForSupport = isCityAggregateView ? cityAggregateAllDeals : metrologistWork.work;
                    const eligibleDeals = dedupeEligibleDeals(allDealsForSupport || []);
                    const bankTaxPercent = Number(matchedServiceCost?.bankReceiptTaxPercent || 0);
                    const bankTaxAmount = Number(((overall.bank * bankTaxPercent) / 100).toFixed(0));
                    const perRequestRate = Number(matchedServiceCost?.perRequestExpense || 0);
                    const dispatcherRate = Number(matchedServiceCost?.dispatcherExpense || 0);
                    const perRequestCount = eligibleDeals.length;
                    const perRequestAmount = Number((perRequestCount * perRequestRate).toFixed(0));
                    const dispatcherCount = eligibleDeals.length;
                    const dispatcherAmount = Number((dispatcherCount * dispatcherRate).toFixed(0));

                    return (
                      <div className="space-y-3">
                        <div className="rounded-lg border border-base-300 overflow-x-auto">
                          <table className="table table-sm w-full">
                            <thead>
                            <tr className="bg-[#1f5e2f] text-white">
                              <th className="min-w-[230px]">Показатель</th>
                              <th className="text-right">СУММА</th>
                              <th className="text-right">Наличные</th>
                              <th className="text-right">р/сч</th>
                              <th className="text-right">Кол-во</th>
                              <th className="text-right">Себестоимость</th>
                              <th className="text-right">ЗП расходы</th>
                              <th className="text-right">Маржа</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr className="bg-[#f7d7dc] font-semibold text-base-content">
                              <td>ПЛАН:</td>
                              <td className="text-right">{rub(metrologistPlanTotal)}</td>
                              <td className="text-right">0</td>
                              <td className="text-right">0</td>
                              <td className="text-right">0</td>
                              <td className="text-right">0</td>
                              <td className="text-right">0</td>
                              <td className="text-right">0</td>
                            </tr>
                            <tr className="bg-[#245f9f] text-white font-bold">
                              <td>ВЫРУЧКА ИТОГО:</td>
                              <td className="text-right">{rub(overall.sum)}</td>
                              <td className="text-right">{rub(overall.cash)}</td>
                              <td className="text-right">{rub(overall.bank)}</td>
                              <td className="text-right">{qty(overall.qty)}</td>
                              <td className="text-right">{rub(overall.cost)}</td>
                              <td className="text-right">{rub(overall.salary)}</td>
                              <td className={`text-right ${marginClass(overallMargin)}`}>{rub(overallMargin)}</td>
                            </tr>

                            <tr className="bg-[#1f4d8a] text-white font-bold">
                              <td>1. ИТОГО Водосчетчики:</td>
                              <td className="text-right">{rub(sectionTotals.water.sum)}</td>
                              <td className="text-right">{rub(sectionTotals.water.cash)}</td>
                              <td className="text-right">{rub(sectionTotals.water.bank)}</td>
                              <td className="text-right">{qty(sectionTotals.water.qty)}</td>
                              <td className="text-right">{rub(sectionTotals.water.cost)}</td>
                              <td className="text-right">{rub(sectionTotals.water.salary)}</td>
                              <td
                                className={`text-right ${marginClass(
                                  sectionTotals.water.sum - sectionTotals.water.cost - sectionTotals.water.salary,
                                )}`}
                              >
                                {rub(sectionTotals.water.sum - sectionTotals.water.cost - sectionTotals.water.salary)}
                              </td>
                            </tr>
                            {waterRows.map((rowKey) => {
                              const cash = waterAmountForRow(metrologistWork.summary.water.nonBank, rowKey);
                              const bank = waterAmountForRow(metrologistWork.summary.water.bankAccount, rowKey);
                              const sum = cash + bank;
                              const qtyValue =
                                waterQtyForRow(metrologistWork.summary.water.nonBank, rowKey) +
                                waterQtyForRow(metrologistWork.summary.water.bankAccount, rowKey);
                              const salary =
                                waterAccrualForRow(metrologistWork.summary.water.nonBank, rowKey) +
                                waterAccrualForRow(metrologistWork.summary.water.bankAccount, rowKey);
                              const cost = qtyValue * getWaterUnitCost(rowKey);
                              const margin = sum - cost - salary;
                              return (
                                <tr key={`water-pivot-${rowKey}`} className="bg-[#d7e8fa]">
                                  <td>{`ВС ${DEFAULT_METROLOGIST_ROW_LABELS.water[rowKey].toLowerCase()}`}</td>
                                  <td className="text-right">{rub(sum)}</td>
                                  <td className="text-right">{rub(cash)}</td>
                                  <td className="text-right">{rub(bank)}</td>
                                  <td className="text-right">{qty(qtyValue)}</td>
                                  <td className="text-right">{rub(cost)}</td>
                                  <td className="text-right">{rub(salary)}</td>
                                  <td className={`text-right ${marginClass(margin)}`}>{rub(margin)}</td>
                                </tr>
                              );
                            })}

                            <tr className="bg-[#89630b] text-white font-bold">
                              <td>2. ИТОГО Теплосчетчики:</td>
                              <td className="text-right">{rub(sectionTotals.heat.sum)}</td>
                              <td className="text-right">{rub(sectionTotals.heat.cash)}</td>
                              <td className="text-right">{rub(sectionTotals.heat.bank)}</td>
                              <td className="text-right">{qty(sectionTotals.heat.qty)}</td>
                              <td className="text-right">{rub(sectionTotals.heat.cost)}</td>
                              <td className="text-right">{rub(sectionTotals.heat.salary)}</td>
                              <td
                                className={`text-right ${marginClass(
                                  sectionTotals.heat.sum - sectionTotals.heat.cost - sectionTotals.heat.salary,
                                )}`}
                              >
                                {rub(sectionTotals.heat.sum - sectionTotals.heat.cost - sectionTotals.heat.salary)}
                              </td>
                            </tr>
                            {heatRows.map((rowKey) => {
                              const cash = heatAmountForRow(metrologistWork.summary.heat.nonBank, rowKey);
                              const bank = heatAmountForRow(metrologistWork.summary.heat.bankAccount, rowKey);
                              const sum = cash + bank;
                              const qtyValue =
                                heatQtyForRow(metrologistWork.summary.heat.nonBank, rowKey) +
                                heatQtyForRow(metrologistWork.summary.heat.bankAccount, rowKey);
                              const salary =
                                heatAccrualForRow(metrologistWork.summary.heat.nonBank, rowKey) +
                                heatAccrualForRow(metrologistWork.summary.heat.bankAccount, rowKey);
                              const cost = qtyValue * getHeatUnitCost(rowKey);
                              const margin = sum - cost - salary;
                              return (
                                <tr key={`heat-pivot-${rowKey}`} className="bg-[#f7e8d5]">
                                  <td>{`ТС ${DEFAULT_METROLOGIST_ROW_LABELS.heat[rowKey].toLowerCase()}`}</td>
                                  <td className="text-right">{rub(sum)}</td>
                                  <td className="text-right">{rub(cash)}</td>
                                  <td className="text-right">{rub(bank)}</td>
                                  <td className="text-right">{qty(qtyValue)}</td>
                                  <td className="text-right">{rub(cost)}</td>
                                  <td className="text-right">{rub(salary)}</td>
                                  <td className={`text-right ${marginClass(margin)}`}>{rub(margin)}</td>
                                </tr>
                              );
                            })}

                            <tr className="bg-[#1f4d8a] text-white font-bold">
                              <td>3. ИТОГО Газ:</td>
                              <td className="text-right">{rub(sectionTotals.gas.sum)}</td>
                              <td className="text-right">{rub(sectionTotals.gas.cash)}</td>
                              <td className="text-right">{rub(sectionTotals.gas.bank)}</td>
                              <td className="text-right">{qty(sectionTotals.gas.qty)}</td>
                              <td className="text-right">{rub(sectionTotals.gas.cost)}</td>
                              <td className="text-right">{rub(sectionTotals.gas.salary)}</td>
                              <td
                                className={`text-right ${marginClass(
                                  sectionTotals.gas.sum - sectionTotals.gas.cost - sectionTotals.gas.salary,
                                )}`}
                              >
                                {rub(sectionTotals.gas.sum - sectionTotals.gas.cost - sectionTotals.gas.salary)}
                              </td>
                            </tr>
                            {gasRows.map((rowKey) => {
                              const cash = metrologistWork.summary.gas.nonBank.revenueTotal;
                              const bank = metrologistWork.summary.gas.bankAccount.revenueTotal;
                              const sum = cash + bank;
                              const qtyValue =
                                metrologistWork.summary.gas.nonBank.verification.quantity +
                                metrologistWork.summary.gas.bankAccount.verification.quantity;
                              const salary =
                                metrologistWork.summary.gas.nonBank.accrualTotal +
                                metrologistWork.summary.gas.bankAccount.accrualTotal;
                              const cost = qtyValue * getGasUnitCost();
                              const margin = sum - cost - salary;
                              return (
                                <tr key={`gas-pivot-${rowKey}`} className="bg-[#dcefd7]">
                                  <td>{`ГС ${DEFAULT_METROLOGIST_ROW_LABELS.gas[rowKey].toLowerCase()}`}</td>
                                  <td className="text-right">{rub(sum)}</td>
                                  <td className="text-right">{rub(cash)}</td>
                                  <td className="text-right">{rub(bank)}</td>
                                  <td className="text-right">{qty(qtyValue)}</td>
                                  <td className="text-right">{rub(cost)}</td>
                                  <td className="text-right">{rub(salary)}</td>
                                  <td className={`text-right ${marginClass(margin)}`}>{rub(margin)}</td>
                                </tr>
                              );
                            })}
                            <tr className="bg-[#1f4d8a] text-white font-bold">
                              <td>РАСХОДЫ ИТОГО:</td>
                              <td className="text-right">{rub(metrologistExpensesTotal)}</td>
                              <td className="text-right text-white/70">—</td>
                              <td className="text-right text-white/70">—</td>
                              <td className="text-right text-white/70">—</td>
                              <td className="text-right text-white/70">—</td>
                              <td className="text-right text-white/70">—</td>
                              <td className="text-right text-white/70">—</td>
                            </tr>
                            <tr className="bg-[#5ea84a] text-white font-bold">
                              <td>Сумма для расчета премии:</td>
                              <td className={`text-right ${premiumBaseClass}`}>{rub(premiumBase)}</td>
                              <td className="text-right text-white/70">—</td>
                              <td className="text-right text-white/70">—</td>
                              <td className="text-right text-white/70">—</td>
                              <td className="text-right text-white/70">—</td>
                              <td className="text-right text-white/70">—</td>
                              <td className="text-right text-white/70">—</td>
                            </tr>
                            </tbody>
                          </table>
                        </div>
                        <div className="rounded-lg border border-[#c65911]/40 overflow-x-auto">
                          <table className="table table-sm w-full">
                            <thead>
                              <tr className="bg-[#c65911] text-white">
                                <th className="min-w-[260px]">Расходы на сопровождение</th>
                                <th className="text-right">Сумма</th>
                                <th className="text-right">Тариф</th>
                                <th className="text-right">Количество</th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr className="bg-[#fff2cc]">
                                <td>Поступления на р/сч</td>
                                <td className="text-right">{rub(bankTaxAmount)}</td>
                                <td className="text-right">{bankTaxPercent}%</td>
                                <td className="text-right">—</td>
                              </tr>
                              <tr className="bg-[#fff8db]">
                                <td>Телефония, Битрикс, Бухгалтерия</td>
                                <td className="text-right">{rub(perRequestAmount)}</td>
                                <td className="text-right">{rub(perRequestRate)}</td>
                                <td className="text-right">{qty(perRequestCount)}</td>
                              </tr>
                              <tr className="bg-[#fff8db]">
                                <td>Диспетчеризация</td>
                                <td className="text-right">{rub(dispatcherAmount)}</td>
                                <td className="text-right">{rub(dispatcherRate)}</td>
                                <td className="text-right">{qty(dispatcherCount)}</td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              </div>

            {isCityAggregateView && (
              <div className="rounded-lg border border-sky-300/60 bg-sky-50/40 overflow-x-auto">
                <div className="grid grid-cols-3 bg-sky-700 text-white text-sm font-semibold">
                  <div className="px-3 py-2 text-left">Сколько денег в кассе</div>
                  <div className="px-3 py-2 text-right">Выручку сдали</div>
                  <div className="px-3 py-2 text-right">Осталось сдать</div>
                </div>
                {cityCashSummary ? (
                  <div className="grid grid-cols-3 bg-sky-100/80 text-sm font-semibold">
                    <div className="px-3 py-2 text-left">{rub(cityCashSummary.cashInRegister)}</div>
                    <div className="px-3 py-2 text-right">{rub(cityCashSummary.surrenderedRevenueAllTime)}</div>
                    <div className="px-3 py-2 text-right">{rub(cityCashSummary.leftToHandoverPeriod)}</div>
                  </div>
                ) : (
                  <div className="px-3 py-3 text-center text-sm text-base-content/70">
                    Нет данных по кассе за выбранный период
                  </div>
                )}
              </div>
            )}

            {(isCityAggregateView || metrologistFinanceRows.length > 0) && (
              <div className="rounded-lg border border-emerald-300/60 bg-emerald-50/40 overflow-x-auto">
                <table className="table table-sm w-full table-fixed">
                  <thead>
                    <tr className="bg-emerald-700 text-white">
                      <th className="w-[24%]">ФИО сотрудника</th>
                      <th className="text-right">ЗП начислено</th>
                      <th className="text-right">Не сданная выручка</th>
                      <th className="text-right">Выручка наличные</th>
                      <th className="text-right">Выручка на р/сч</th>
                      <th className="text-right">Итого выручка</th>
                      <th className="text-right">ГСМ</th>
                    </tr>
                  </thead>
                  <tbody>
                    {metrologistFinanceRows.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="text-center text-base-content/70">
                          Нет данных по сотрудникам за выбранный период
                        </td>
                      </tr>
                    ) : (
                      metrologistFinanceRows.map((row) => (
                        <tr key={row.employeeId} className={`${isCityAggregateView ? '' : 'font-medium'} even:bg-emerald-100/40`}>
                          <td>{row.fullName}</td>
                          <td className="text-right">{rub(row.salaryAccrued)}</td>
                          <td className="text-right">{rub(row.unsurrenderedRevenue)}</td>
                          <td className="text-right">{rub(row.cashRevenue)}</td>
                          <td className="text-right">{rub(row.bankRevenue)}</td>
                          <td className="text-right">{rub(row.totalRevenue)}</td>
                          <td className="text-right">{rub(row.gsm)}</td>
                        </tr>
                      ))
                    )}
                    <tr className="bg-emerald-200/80 font-bold">
                      <td>Итого</td>
                      <td className="text-right">
                        {rub(metrologistFinanceRows.reduce((sum, row) => sum + Number(row.salaryAccrued || 0), 0))}
                      </td>
                      <td className="text-right">
                        {rub(metrologistFinanceRows.reduce((sum, row) => sum + Number(row.unsurrenderedRevenue || 0), 0))}
                      </td>
                      <td className="text-right">
                        {rub(metrologistFinanceRows.reduce((sum, row) => sum + Number(row.cashRevenue || 0), 0))}
                      </td>
                      <td className="text-right">
                        {rub(metrologistFinanceRows.reduce((sum, row) => sum + Number(row.bankRevenue || 0), 0))}
                      </td>
                      <td className="text-right">
                        {rub(metrologistFinanceRows.reduce((sum, row) => sum + Number(row.totalRevenue || 0), 0))}
                      </td>
                      <td className="text-right">
                        {rub(metrologistFinanceRows.reduce((sum, row) => sum + Number(row.gsm || 0), 0))}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            )}

              <div className="overflow-x-auto">
                <table className="table table-zebra w-full">
                  <thead>
                    <tr className="bg-base-200">
                      <th>Дата закрытия</th>
                      <th>Название сделки</th>
                      <th>Оплата</th>
                      <th>Услуга</th>
                      <th>Кол-во</th>
                      <th>Цена</th>
                      <th>Начисление</th>
                    </tr>
                  </thead>
                  <tbody>
                    {metrologistWork.work.length === 0 && (
                      <tr>
                        <td colSpan={7} className="text-center py-8 text-base-content/70">
                          За выбранный период данных нет
                        </td>
                      </tr>
                    )}

                    {metrologistWork.work.flatMap((deal) => {
                      if (!deal.products || deal.products.length === 0) {
                        return [
                          <tr key={`${deal.dealId}-empty`}>
                            <td>{deal.closedAt ? new Date(deal.closedAt).toLocaleString('ru-RU') : '-'}</td>
                            <td className="font-medium max-w-xs">
                              {deal.dealUrl ? (
                                <a
                                  href={deal.dealUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="link link-primary"
                                >
                                  {deal.title}
                                </a>
                              ) : (
                                deal.title
                              )}
                            </td>
                            <td>{deal.paymentByBankAccount ? 'Р/сч' : '—'}</td>
                            <td className="text-base-content/70">Без товаров</td>
                            <td>0</td>
                            <td>0</td>
                            <td className="font-medium">0</td>
                          </tr>,
                        ];
                      }

                      return deal.products.map((product, index) => (
                        <tr key={`${deal.dealId}-${product.variantId}-${index}`}>
                          <td>{deal.closedAt ? new Date(deal.closedAt).toLocaleString('ru-RU') : '-'}</td>
                          <td className="font-medium max-w-xs">
                            {index === 0 ? (
                              deal.dealUrl ? (
                                <a
                                  href={deal.dealUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="link link-primary"
                                >
                                  {deal.title}
                                </a>
                              ) : (
                                deal.title
                              )
                            ) : (
                              ''
                            )}
                          </td>
                          <td>{index === 0 ? (deal.paymentByBankAccount ? 'Р/сч' : '—') : ''}</td>
                          <td>
                            <div className="leading-tight">
                              <div className="font-medium">{product.serviceName || product.name}</div>
                              <div className="text-xs text-base-content/70">{product.variantName || product.name}</div>
                            </div>
                          </td>
                          <td>{product.quantity}</td>
                          <td>{product.price.toLocaleString('ru-RU')}</td>
                          <td className="font-medium">{product.reward.toLocaleString('ru-RU')}</td>
                        </tr>
                      ));
                    })}
                  </tbody>
                  <tfoot>
                    <tr className="bg-base-200">
                      <td colSpan={6} className="text-right font-semibold">
                        Итого
                      </td>
                      <td className="font-bold">{metrologistWork.totalReward.toLocaleString('ru-RU')} руб.</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
              {metrologistWork.pagination && metrologistWork.pagination.totalPages > 1 && (
                <div className="flex items-center justify-between gap-3">
                  <div className="text-sm text-base-content/70">
                    Страница {metrologistWork.pagination.page} из {metrologistWork.pagination.totalPages}
                    {' '}({metrologistWork.pagination.totalItems} сделок)
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      className="btn btn-sm"
                      onClick={() => handleMetrologistPageChange(metrologistPage - 1)}
                      disabled={!metrologistWork.pagination.hasPrev || metrologistLoading}
                    >
                      Назад
                    </button>
                    <button
                      className="btn btn-sm"
                      onClick={() => handleMetrologistPageChange(metrologistPage + 1)}
                      disabled={!metrologistWork.pagination.hasNext || metrologistLoading}
                    >
                      Вперед
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {isAdminUser && activeTab === 'auditConsole' && (
        <div className="space-y-6">
          <div>
            <h2 className="text-xl md:text-2xl font-bold text-base-content">Консоль изменений</h2>
            <p className="text-sm md:text-base font-medium text-base-content/70 mt-2">
              Журнал создания, редактирования и удаления записей в разделах отчёта. По умолчанию — действия аккаунтов «Зарплата».
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-7 gap-3 items-end">
            <label className="form-control">
              <span className="label-text">Роль</span>
              <Dropdown
                value={auditFilterActorType}
                onChange={(e) => setAuditFilterActorType((e.value as 'admin' | 'salary' | '') ?? 'salary')}
                options={auditActorTypeOptions}
                optionLabel="label"
                optionValue="value"
                className="w-full"
              />
            </label>
            <label className="form-control">
              <span className="label-text">Раздел</span>
              <Dropdown
                value={auditFilterEntityType}
                onChange={(e) => setAuditFilterEntityType((e.value as ReportEntryEntityType | '') || '')}
                options={auditEntityTypeOptions}
                optionLabel="label"
                optionValue="value"
                placeholder="Все разделы"
                className="w-full"
              />
            </label>
            <label className="form-control">
              <span className="label-text">Город</span>
              <Dropdown
                value={auditFilterCity}
                onChange={(e) => setAuditFilterCity(e.value || '')}
                options={developmentCityOptions}
                optionLabel="label"
                optionValue="value"
                placeholder="Все города"
                className="w-full"
                showClear
              />
            </label>
            <label className="form-control">
              <span className="label-text">Пользователь</span>
              <input
                type="text"
                className="input input-bordered"
                value={auditFilterActorLogin}
                onChange={(e) => setAuditFilterActorLogin(e.target.value)}
                placeholder="Логин"
              />
            </label>
            <label className="form-control">
              <span className="label-text">С</span>
              <input
                type="date"
                className="input input-bordered"
                value={auditFilterFrom}
                onChange={(e) => setAuditFilterFrom(e.target.value)}
              />
            </label>
            <label className="form-control">
              <span className="label-text">По</span>
              <input
                type="date"
                className="input input-bordered"
                value={auditFilterTo}
                onChange={(e) => setAuditFilterTo(e.target.value)}
              />
            </label>
            <button className="btn btn-primary" onClick={() => void loadAuditLogs()} disabled={auditLoading}>
              {auditLoading ? 'Загрузка…' : 'Показать'}
            </button>
          </div>

          <div className="flex items-center justify-between text-xs text-base-content/70">
            <span>
              Показано{' '}
              {filteredHouseholdRows.length === 0
                ? 0
                : (safeHouseholdOperationsPage - 1) * householdOperationsPageSize + 1}
              {' — '}
              {Math.min(safeHouseholdOperationsPage * householdOperationsPageSize, filteredHouseholdRows.length)} из{' '}
              {filteredHouseholdRows.length}
            </span>
            <div className="flex items-center gap-2">
              <button
                className="btn btn-xs btn-ghost"
                onClick={() => setHouseholdOperationsPage((prev) => Math.max(1, prev - 1))}
                disabled={safeHouseholdOperationsPage <= 1}
              >
                Назад
              </button>
              <span>
                Стр. {safeHouseholdOperationsPage} / {householdOperationsTotalPages}
              </span>
              <button
                className="btn btn-xs btn-ghost"
                onClick={() => setHouseholdOperationsPage((prev) => Math.min(householdOperationsTotalPages, prev + 1))}
                disabled={safeHouseholdOperationsPage >= householdOperationsTotalPages}
              >
                Вперед
              </button>
            </div>
          </div>

          <div className="rounded-lg border border-base-300 overflow-x-auto">
            <table className="table table-sm w-full text-sm">
              <thead>
                <tr className="bg-base-200">
                  <th>Когда</th>
                  <th>Пользователь</th>
                  <th>Роль</th>
                  <th>Раздел</th>
                  <th>Действие</th>
                  <th>Город</th>
                  <th>Детали</th>
                </tr>
              </thead>
              <tbody>
                {auditLogs.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="text-center text-base-content/60 py-6">
                      {auditLoading ? 'Загрузка…' : 'Записей не найдено'}
                    </td>
                  </tr>
                ) : (
                  auditLogs.map((log) => (
                    <tr key={log.id}>
                      <td className="whitespace-nowrap">{formatAuditDateTime(log.createdAt)}</td>
                      <td>{log.actorLogin}</td>
                      <td>{AUDIT_ACTOR_TYPE_LABELS[log.actorType] || log.actorType}</td>
                      <td>{REPORT_ENTRY_ENTITY_LABELS[log.entityType] || log.entityType}</td>
                      <td>{REPORT_ENTRY_ACTION_LABELS[log.action] || log.action}</td>
                      <td>{log.city || '—'}</td>
                      <td className="max-w-xl whitespace-normal">{describeAuditChange(log)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeTab === 'bonusExtras' && (
        <div className="space-y-6">
          <div>
            <h2 className="text-xl md:text-2xl font-bold text-base-content">Доп начисления</h2>
            <p className="text-sm md:text-base font-medium text-base-content/70 mt-2">
              Выбор города, сотрудника, суммы, даты и комментария.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-3 items-end">
            <label className="form-control">
              <span className="label-text">Город</span>
              <Dropdown
                value={bonusExtraForm.city}
                onChange={(e) => {
                  const city = e.value || '';
                  if (editingBonusExtraId) {
                    setBonusExtraForm((prev) => ({ ...prev, city, employeeId: '' }));
                  }
                  applySharedCity(city);
                }}
                options={developmentCityOptions}
                optionLabel="label"
                optionValue="value"
                placeholder="Выберите город"
                className="w-full"
                showClear
              />
            </label>
            <label className="form-control md:col-span-2">
              <span className="label-text">Сотрудник (необязательно)</span>
              <Dropdown
                value={bonusExtraForm.employeeId}
                onChange={(e) => {
                  const employeeId = e.value || '';
                  if (editingBonusExtraId) {
                    setBonusExtraForm((prev) => ({ ...prev, employeeId }));
                  }
                  applySharedEmployee(employeeId);
                }}
                options={metrologistsBySharedCity.map((employee) => ({
                  label: `${employee.fullName}${employee.city ? ` (${employee.city})` : ''}`,
                  value: employee.id,
                }))}
                optionLabel="label"
                optionValue="value"
                placeholder={bonusExtraForm.city ? 'Выберите сотрудника' : 'Сначала выберите город'}
                className="w-full"
                showClear
                disabled={!bonusExtraForm.city}
              />
            </label>
            <label className="form-control">
              <span className="label-text">Сумма</span>
              <input
                type="number"
                className="input input-bordered [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                value={bonusExtraForm.amount}
                onChange={(e) => setBonusExtraForm((prev) => ({ ...prev, amount: e.target.value }))}
                onWheel={(e) => e.currentTarget.blur()}
                placeholder="0"
              />
            </label>
            <label className="form-control">
              <span className="label-text">Дата</span>
              <input
                type="date"
                className="input input-bordered"
                value={bonusExtraForm.date}
                onChange={(e) => setBonusExtraForm((prev) => ({ ...prev, date: e.target.value }))}
              />
            </label>
            <label className="form-control md:col-span-4">
              <span className="label-text">Комментарий (необязательно)</span>
              <input
                type="text"
                className="input input-bordered"
                value={bonusExtraForm.comment}
                onChange={(e) => setBonusExtraForm((prev) => ({ ...prev, comment: e.target.value }))}
                onWheel={(e) => e.currentTarget.blur()}
              />
            </label>
            <button className="btn btn-primary" onClick={() => void handleCreateBonusExtra()} disabled={developmentLoading}>
              {editingBonusExtraId ? 'Сохранить' : 'Добавить'}
            </button>
            {editingBonusExtraId && (
              <button
                className="btn btn-ghost"
                onClick={() => {
                  setEditingBonusExtraId(null);
                  setBonusExtraForm(developmentFormWithSharedSelection());
                }}
                disabled={developmentLoading}
              >
                Отмена
              </button>
            )}
          </div>
          <div className="rounded-lg border border-base-300 overflow-x-auto">
            <table className="table table-sm w-full text-sm">
              <thead>
                <tr className="bg-base-200">
                  <th>Дата</th>
                  <th>Город</th>
                  <th>Сотрудник</th>
                  <th className="text-right">Сумма</th>
                  <th>Комментарий</th>
                  <th className="text-right w-44">Действия</th>
                </tr>
              </thead>
              <tbody>
                {bonusExtraRows.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="text-center text-base-content/70">
                      Пока нет записей
                    </td>
                  </tr>
                ) : (
                  bonusExtraRows.map((row) => (
                    <tr key={row.id}>
                      <td>{new Date(row.date).toLocaleDateString('ru-RU')}</td>
                      <td>{row.city}</td>
                      <td>{row.employee?.fullName || '—'}</td>
                      <td className="text-right">{rub(Number(row.amount || 0))}</td>
                      <td>{row.comment || '—'}</td>
                      <td className="text-right whitespace-nowrap space-x-2">
                        <button
                          className="btn btn-xs btn-outline"
                          onClick={() => handleEditBonusExtra(row)}
                          disabled={developmentLoading}
                        >
                          Редактировать
                        </button>
                        <button
                          className="btn btn-xs btn-error btn-outline"
                          onClick={() => void handleDeleteBonusExtra(row.id)}
                          disabled={developmentLoading}
                        >
                          Удалить
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeTab === 'gsmDev' && (
        <div className="space-y-6">
          <div>
            <h2 className="text-xl md:text-2xl font-bold text-base-content">ГСМ</h2>
            <p className="text-sm md:text-base font-medium text-base-content/70 mt-2">
              Учет ГСМ по городу, сотруднику, сумме, дате и комментарию.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-3 items-end">
            <label className="form-control">
              <span className="label-text">Город</span>
              <Dropdown
                value={gsmForm.city}
                onChange={(e) => {
                  const city = e.value || '';
                  if (editingGsmId) {
                    setGsmForm((prev) => ({ ...prev, city, employeeId: '' }));
                  }
                  applySharedCity(city);
                }}
                options={developmentCityOptions}
                optionLabel="label"
                optionValue="value"
                placeholder="Выберите город"
                className="w-full"
                showClear
              />
            </label>
            <label className="form-control md:col-span-2">
              <span className="label-text">Сотрудник</span>
              <Dropdown
                value={gsmForm.employeeId}
                onChange={(e) => {
                  const employeeId = e.value || '';
                  if (editingGsmId) {
                    setGsmForm((prev) => ({ ...prev, employeeId }));
                  }
                  applySharedEmployee(employeeId);
                }}
                options={metrologistsBySharedCity.map((employee) => ({
                  label: `${employee.fullName}${employee.city ? ` (${employee.city})` : ''}`,
                  value: employee.id,
                }))}
                optionLabel="label"
                optionValue="value"
                placeholder={gsmForm.city ? 'Выберите сотрудника' : 'Сначала выберите город'}
                className="w-full"
                showClear
                disabled={!gsmForm.city}
              />
            </label>
            <label className="form-control">
              <span className="label-text">Сумма</span>
              <input
                type="number"
                className="input input-bordered [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                value={gsmForm.amount}
                onChange={(e) => setGsmForm((prev) => ({ ...prev, amount: e.target.value }))}
                onWheel={(e) => e.currentTarget.blur()}
                placeholder="0"
              />
            </label>
            <label className="form-control">
              <span className="label-text">Дата</span>
              <input
                type="date"
                className="input input-bordered"
                value={gsmForm.date}
                onChange={(e) => setGsmForm((prev) => ({ ...prev, date: e.target.value }))}
              />
            </label>
            <label className="form-control md:col-span-4">
              <span className="label-text">Комментарий (необязательно)</span>
              <input
                type="text"
                className="input input-bordered"
                value={gsmForm.comment}
                onChange={(e) => setGsmForm((prev) => ({ ...prev, comment: e.target.value }))}
                onWheel={(e) => e.currentTarget.blur()}
              />
            </label>
            <button className="btn btn-primary" onClick={() => void handleCreateGsm()} disabled={developmentLoading}>
              {editingGsmId ? 'Сохранить' : 'Добавить'}
            </button>
            {editingGsmId && (
              <button
                className="btn btn-ghost"
                onClick={() => {
                  setEditingGsmId(null);
                  setGsmForm(developmentFormWithSharedSelection());
                }}
                disabled={developmentLoading}
              >
                Отмена
              </button>
            )}
          </div>
          <div className="rounded-lg border border-base-300 overflow-x-auto">
            <table className="table table-sm w-full text-sm">
              <thead>
                <tr className="bg-base-200">
                  <th>Дата</th>
                  <th>Город</th>
                  <th>Сотрудник</th>
                  <th className="text-right">Сумма</th>
                  <th>Комментарий</th>
                  <th className="text-right w-44">Действия</th>
                </tr>
              </thead>
              <tbody>
                {gsmRows.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="text-center text-base-content/70">
                      Пока нет записей
                    </td>
                  </tr>
                ) : (
                  gsmRows.map((row) => (
                    <tr key={row.id}>
                      <td>{new Date(row.date).toLocaleDateString('ru-RU')}</td>
                      <td>{row.city}</td>
                      <td>{row.employee?.fullName || '—'}</td>
                      <td className="text-right">{rub(Number(row.amount || 0))}</td>
                      <td>{row.comment || '—'}</td>
                      <td className="text-right whitespace-nowrap space-x-2">
                        <button
                          className="btn btn-xs btn-outline"
                          onClick={() => handleEditGsm(row)}
                          disabled={developmentLoading}
                        >
                          Редактировать
                        </button>
                        <button
                          className="btn btn-xs btn-error btn-outline"
                          onClick={() => void handleDeleteGsm(row.id)}
                          disabled={developmentLoading}
                        >
                          Удалить
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeTab === 'revenueHandovers' && (
        <div className="space-y-6">
          <div>
            <h2 className="text-xl md:text-2xl font-bold text-base-content">Сдача выручки</h2>
            <p className="text-sm md:text-base font-medium text-base-content/70 mt-2">
              Выбор города, сотрудника, суммы, даты и комментария.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-3 items-end">
            <label className="form-control">
              <span className="label-text">Город</span>
              <Dropdown
                value={revenueHandoverForm.city}
                onChange={(e) => {
                  const city = e.value || '';
                  if (editingRevenueHandoverId) {
                    setRevenueHandoverForm((prev) => ({ ...prev, city, employeeId: '' }));
                  }
                  applySharedCity(city);
                }}
                options={developmentCityOptions}
                optionLabel="label"
                optionValue="value"
                placeholder="Выберите город"
                className="w-full"
                showClear
              />
            </label>
            <label className="form-control md:col-span-2">
              <span className="label-text">Сотрудник</span>
              <Dropdown
                value={revenueHandoverForm.employeeId}
                onChange={(e) => {
                  const employeeId = e.value || '';
                  if (editingRevenueHandoverId) {
                    setRevenueHandoverForm((prev) => ({ ...prev, employeeId }));
                  }
                  applySharedEmployee(employeeId);
                }}
                options={metrologistsBySharedCity.map((employee) => ({
                  label: `${employee.fullName}${employee.city ? ` (${employee.city})` : ''}`,
                  value: employee.id,
                }))}
                optionLabel="label"
                optionValue="value"
                placeholder={revenueHandoverForm.city ? 'Выберите сотрудника' : 'Сначала выберите город'}
                className="w-full"
                showClear
                disabled={!revenueHandoverForm.city}
              />
            </label>
            <label className="form-control">
              <span className="label-text">Сумма</span>
              <input
                type="number"
                className="input input-bordered [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                value={revenueHandoverForm.amount}
                onChange={(e) => setRevenueHandoverForm((prev) => ({ ...prev, amount: e.target.value }))}
                onWheel={(e) => e.currentTarget.blur()}
                placeholder="0"
              />
            </label>
            <label className="form-control">
              <span className="label-text">Дата</span>
              <input
                type="date"
                className="input input-bordered"
                value={revenueHandoverForm.date}
                onChange={(e) => setRevenueHandoverForm((prev) => ({ ...prev, date: e.target.value }))}
              />
            </label>
            <label className="form-control md:col-span-4">
              <span className="label-text">Комментарий (необязательно)</span>
              <input
                type="text"
                className="input input-bordered"
                value={revenueHandoverForm.comment}
                onChange={(e) => setRevenueHandoverForm((prev) => ({ ...prev, comment: e.target.value }))}
                onWheel={(e) => e.currentTarget.blur()}
              />
            </label>
            <button className="btn btn-primary" onClick={() => void handleCreateRevenueHandover()} disabled={developmentLoading}>
              {editingRevenueHandoverId ? 'Сохранить' : 'Добавить'}
            </button>
            {editingRevenueHandoverId && (
              <button
                className="btn btn-ghost"
                onClick={() => {
                  setEditingRevenueHandoverId(null);
                  setRevenueHandoverForm(developmentFormWithSharedSelection());
                }}
                disabled={developmentLoading}
              >
                Отмена
              </button>
            )}
          </div>
          <div className="rounded-lg border border-base-300 overflow-x-auto">
            <table className="table table-sm w-full text-sm">
              <thead>
                <tr className="bg-base-200">
                  <th>Дата</th>
                  <th>Город</th>
                  <th>Сотрудник</th>
                  <th className="text-right">Сумма</th>
                  <th>Комментарий</th>
                  <th className="text-right w-44">Действия</th>
                </tr>
              </thead>
              <tbody>
                {revenueHandoverRows.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="text-center text-base-content/70">
                      Пока нет записей
                    </td>
                  </tr>
                ) : (
                  revenueHandoverRows.map((row) => (
                    <tr key={row.id}>
                      <td>{new Date(row.date).toLocaleDateString('ru-RU')}</td>
                      <td>{row.city}</td>
                      <td>{row.employee?.fullName || '—'}</td>
                      <td className="text-right">{rub(Number(row.amount || 0))}</td>
                      <td>{row.comment || '—'}</td>
                      <td className="text-right whitespace-nowrap space-x-2">
                        <button
                          className="btn btn-xs btn-outline"
                          onClick={() => handleEditRevenueHandover(row)}
                          disabled={developmentLoading}
                        >
                          Редактировать
                        </button>
                        <button
                          className="btn btn-xs btn-error btn-outline"
                          onClick={() => void handleDeleteRevenueHandover(row.id)}
                          disabled={developmentLoading}
                        >
                          Удалить
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeTab === 'expensesDev' && (
        <div className="space-y-6">
          <div>
            <h2 className="text-xl md:text-2xl font-bold text-base-content">Расходы</h2>
            <p className="text-sm md:text-base font-medium text-base-content/70 mt-2">
              Сотрудник необязателен. Расходы без сотрудника попадают в общую Excel.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-6 gap-3 items-end">
            <label className="form-control">
              <span className="label-text">Город</span>
              <Dropdown
                value={expenseForm.city}
                onChange={(e) => {
                  const city = e.value || '';
                  if (editingExpenseId) {
                    setExpenseForm((prev) => ({ ...prev, city, employeeId: '' }));
                  }
                  applySharedCity(city);
                }}
                options={developmentCityOptions}
                optionLabel="label"
                optionValue="value"
                placeholder="Выберите город"
                className="w-full"
                showClear
              />
            </label>
            <label className="form-control md:col-span-2">
              <span className="label-text">Сотрудник (необязательно)</span>
              <Dropdown
                value={expenseForm.employeeId}
                onChange={(e) => {
                  const employeeId = e.value || '';
                  setExpenseForm((prev) => ({ ...prev, employeeId }));
                  // Пустой сотрудник на расходах — локально, общий выбор не сбрасываем.
                  if (employeeId) {
                    applySharedEmployee(employeeId);
                  }
                }}
                options={metrologistsBySharedCity.map((employee) => ({
                  label: `${employee.fullName}${employee.city ? ` (${employee.city})` : ''}`,
                  value: employee.id,
                }))}
                optionLabel="label"
                optionValue="value"
                placeholder={expenseForm.city ? 'Необязательно' : 'Сначала выберите город'}
                className="w-full"
                showClear
                disabled={!expenseForm.city}
              />
            </label>
            <label className="form-control">
              <span className="label-text">Тип оплаты</span>
              <Dropdown
                value={expenseForm.paymentType}
                onChange={(e) =>
                  setExpenseForm((prev) => ({ ...prev, paymentType: (e.value as 'cash' | 'bank') || 'cash' }))
                }
                options={[
                  { label: 'Наличные', value: 'cash' },
                  { label: 'Безнал', value: 'bank' },
                ]}
                optionLabel="label"
                optionValue="value"
                className="w-full"
              />
            </label>
            <label className="form-control">
              <span className="label-text">Сумма</span>
              <input
                type="number"
                className="input input-bordered [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                value={expenseForm.amount}
                onChange={(e) => setExpenseForm((prev) => ({ ...prev, amount: e.target.value }))}
                onWheel={(e) => e.currentTarget.blur()}
                placeholder="0"
              />
            </label>
            <label className="form-control">
              <span className="label-text">Дата</span>
              <input
                type="date"
                className="input input-bordered"
                value={expenseForm.date}
                onChange={(e) => setExpenseForm((prev) => ({ ...prev, date: e.target.value }))}
              />
            </label>
            <label className="form-control md:col-span-5">
              <span className="label-text">Расшифровка (необязательно)</span>
              <input
                type="text"
                className="input input-bordered"
                value={expenseForm.comment}
                onChange={(e) => setExpenseForm((prev) => ({ ...prev, comment: e.target.value }))}
                onWheel={(e) => e.currentTarget.blur()}
              />
            </label>
            <button className="btn btn-primary" onClick={() => void handleCreateExpense()} disabled={developmentLoading}>
              {editingExpenseId ? 'Сохранить' : 'Добавить'}
            </button>
            {editingExpenseId && (
              <button
                className="btn btn-ghost"
                onClick={() => {
                  setEditingExpenseId(null);
                  setExpenseForm(expenseFormWithSharedSelection());
                }}
                disabled={developmentLoading}
              >
                Отмена
              </button>
            )}
          </div>
          <div className="rounded-lg border border-base-300 overflow-x-auto">
            <table className="table table-sm w-full text-sm">
              <thead>
                <tr className="bg-base-200">
                  <th>Дата</th>
                  <th>Город</th>
                  <th>Сотрудник</th>
                  <th>Тип оплаты</th>
                  <th className="text-right">Сумма</th>
                  <th>Расшифровка</th>
                  <th className="text-right w-44">Действия</th>
                </tr>
              </thead>
              <tbody>
                {expenseRows.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="text-center text-base-content/70">
                      Пока нет записей
                    </td>
                  </tr>
                ) : (
                  expenseRows.map((row) => (
                    <tr key={row.id}>
                      <td>{new Date(row.date).toLocaleDateString('ru-RU')}</td>
                      <td>{row.city}</td>
                      <td>{row.employee?.fullName || '—'}</td>
                      <td>{row.paymentType === 'bank' ? 'Безнал' : 'Наличные'}</td>
                      <td className="text-right">{rub(Number(row.amount || 0))}</td>
                      <td>{row.comment || '—'}</td>
                      <td className="text-right whitespace-nowrap space-x-2">
                        <button
                          className="btn btn-xs btn-outline"
                          onClick={() => handleEditExpense(row)}
                          disabled={developmentLoading}
                        >
                          Редактировать
                        </button>
                        <button
                          className="btn btn-xs btn-error btn-outline"
                          onClick={() => void handleDeleteExpense(row.id)}
                          disabled={developmentLoading}
                        >
                          Удалить
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeTab === 'plansDev' && (
        <div className="space-y-6">
          <div>
            <h2 className="text-xl md:text-2xl font-bold text-base-content">План</h2>
            <p className="text-sm md:text-base font-medium text-base-content/70 mt-2">
              Сумма плана по городу и месяцу. В Excel учитывается на листе «Итог_город».
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
            <label className="form-control">
              <span className="label-text">Город</span>
              <Dropdown
                value={planForm.city}
                onChange={(e) => {
                  const city = e.value || '';
                  if (editingPlanId) {
                    setPlanForm((prev) => ({ ...prev, city }));
                  }
                  applySharedCity(city);
                }}
                options={developmentCityOptions}
                optionLabel="label"
                optionValue="value"
                placeholder="Выберите город"
                className="w-full"
                showClear
              />
            </label>
            <label className="form-control">
              <span className="label-text">Месяц</span>
              <input
                type="month"
                className="input input-bordered"
                value={planForm.month}
                onChange={(e) => setPlanForm((prev) => ({ ...prev, month: e.target.value }))}
              />
            </label>
            <label className="form-control">
              <span className="label-text">Сумма</span>
              <input
                type="number"
                className="input input-bordered [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                value={planForm.amount}
                onChange={(e) => setPlanForm((prev) => ({ ...prev, amount: e.target.value }))}
                onWheel={(e) => e.currentTarget.blur()}
                placeholder="0"
              />
            </label>
            <button className="btn btn-primary" onClick={() => void handleUpsertPlan()} disabled={developmentLoading}>
              {editingPlanId ? 'Сохранить' : 'Сохранить план'}
            </button>
            {editingPlanId && (
              <button
                className="btn btn-ghost"
                onClick={() => {
                  setEditingPlanId(null);
                  setPlanForm({ ...initialPlanForm, city: selectedMetrologistCity });
                }}
                disabled={developmentLoading}
              >
                Отмена
              </button>
            )}
          </div>
          <div className="rounded-lg border border-base-300 overflow-x-auto">
            <table className="table table-sm w-full text-sm">
              <thead>
                <tr className="bg-base-200">
                  <th>Город</th>
                  <th>Месяц</th>
                  <th className="text-right">Сумма</th>
                  <th className="text-right w-44">Действия</th>
                </tr>
              </thead>
              <tbody>
                {planRows.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="text-center text-base-content/70">
                      Пока нет записей
                    </td>
                  </tr>
                ) : (
                  planRows.map((row) => (
                    <tr key={row.id}>
                      <td>{row.city}</td>
                      <td>
                        {MONTH_OPTIONS.find((m) => Number(m.value) === row.month)?.label || row.month} {row.year}
                      </td>
                      <td className="text-right">{rub(Number(row.amount || 0))}</td>
                      <td className="text-right whitespace-nowrap space-x-2">
                        <button
                          className="btn btn-xs btn-outline"
                          onClick={() => handleEditPlan(row)}
                          disabled={developmentLoading}
                        >
                          Редактировать
                        </button>
                        <button
                          className="btn btn-xs btn-error btn-outline"
                          onClick={() => void handleDeletePlan(row.id)}
                          disabled={developmentLoading}
                        >
                          Удалить
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeTab === 'businessTripsDev' && (
        <div className="space-y-6">
          <div>
            <h2 className="text-xl md:text-2xl font-bold text-base-content">Командировки</h2>
            <p className="text-sm md:text-base font-medium text-base-content/70 mt-2">
              Диапазоны дат работы сотрудника в другом городе. Пересечения у одного сотрудника запрещены.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-6 gap-3 items-end">
            <label className="form-control">
              <span className="label-text">Город командировки</span>
              <Dropdown
                value={businessTripForm.city}
                onChange={(e) => {
                  const city = e.value || '';
                  if (editingBusinessTripId) {
                    setBusinessTripForm((prev) => ({ ...prev, city }));
                  }
                  applySharedCity(city);
                }}
                options={developmentCityOptions}
                optionLabel="label"
                optionValue="value"
                placeholder="Выберите город командировки"
                className="w-full"
                showClear
              />
            </label>
            <label className="form-control md:col-span-2">
              <span className="label-text">Сотрудник</span>
              <Dropdown
                value={businessTripForm.employeeId}
                onChange={(e) => {
                  const employeeId = e.value || '';
                  if (editingBusinessTripId) {
                    setBusinessTripForm((prev) => ({ ...prev, employeeId }));
                  }
                  applySharedEmployee(employeeId);
                }}
                options={metrologists.map((employee) => ({
                  label: `${employee.fullName}${employee.city ? ` (${employee.city})` : ''}`,
                  value: employee.id,
                }))}
                optionLabel="label"
                optionValue="value"
                placeholder="Выберите сотрудника"
                className="w-full"
                showClear
              />
            </label>
            <label className="form-control">
              <span className="label-text">С</span>
              <input
                type="date"
                className="input input-bordered"
                value={businessTripForm.from}
                onChange={(e) => setBusinessTripForm((prev) => ({ ...prev, from: e.target.value }))}
              />
            </label>
            <label className="form-control">
              <span className="label-text">По</span>
              <input
                type="date"
                className="input input-bordered"
                value={businessTripForm.to}
                onChange={(e) => setBusinessTripForm((prev) => ({ ...prev, to: e.target.value }))}
              />
            </label>
            <label className="form-control md:col-span-4">
              <span className="label-text">Комментарий (необязательно)</span>
              <input
                type="text"
                className="input input-bordered"
                value={businessTripForm.comment}
                onChange={(e) => setBusinessTripForm((prev) => ({ ...prev, comment: e.target.value }))}
                placeholder="Например: замена в соседнем городе"
              />
            </label>
            <button className="btn btn-primary" onClick={() => void handleCreateBusinessTrip()} disabled={developmentLoading}>
              {editingBusinessTripId ? 'Сохранить' : 'Добавить'}
            </button>
            {editingBusinessTripId && (
              <button
                className="btn btn-ghost"
                onClick={() => {
                  setEditingBusinessTripId(null);
                  setBusinessTripForm(businessTripFormWithSharedSelection());
                }}
                disabled={developmentLoading}
              >
                Отмена
              </button>
            )}
          </div>
          <div className="rounded-lg border border-base-300 overflow-x-auto">
            <table className="table table-sm w-full text-sm">
              <thead>
                <tr className="bg-base-200">
                  <th>Сотрудник</th>
                  <th>Город командировки</th>
                  <th>Период</th>
                  <th>Комментарий</th>
                  <th className="text-right w-44">Действия</th>
                </tr>
              </thead>
              <tbody>
                {filteredBusinessTripRows.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="text-center text-base-content/70">
                      Пока нет записей
                    </td>
                  </tr>
                ) : (
                  filteredBusinessTripRows.map((row) => (
                    <tr key={row.id}>
                      <td>{row.employee?.fullName || '—'}</td>
                      <td>{row.city}</td>
                      <td>
                        {formatDateRu(row.from)} — {formatDateRu(row.to)}
                      </td>
                      <td>{row.comment || '—'}</td>
                      <td className="text-right whitespace-nowrap space-x-2">
                        <button
                          className="btn btn-xs btn-outline"
                          onClick={() => handleEditBusinessTrip(row)}
                          disabled={developmentLoading}
                        >
                          Редактировать
                        </button>
                        <button
                          className="btn btn-xs btn-error btn-outline"
                          onClick={() => void handleDeleteBusinessTrip(row.id)}
                          disabled={developmentLoading}
                        >
                          Удалить
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeTab === 'householdDev' && (
        <div className="space-y-6">
          <div>
            <h2 className="text-xl md:text-2xl font-bold text-base-content">Хоз блок</h2>
            <p className="text-sm md:text-base font-medium text-base-content/70 mt-2">
              Единый журнал движений: склад и персональные остатки метрологов. Выдача и возврат отражаются в обеих
              сторонах, списание по заявкам можно пересчитать за период.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-5 gap-3 items-end">
            <label className="form-control">
              <span className="label-text">Город</span>
              <Dropdown
                value={selectedMetrologistCity}
                onChange={(e) => applySharedCity(e.value || '')}
                options={developmentCityOptions}
                optionLabel="label"
                optionValue="value"
                placeholder="Выберите город"
                className="w-full"
                showClear
              />
            </label>
            <label className="form-control">
              <span className="label-text">Метролог (опционально)</span>
              <Dropdown
                value={selectedMetrologistId}
                onChange={(e) => setSelectedMetrologistId(e.value || '')}
                options={metrologistsBySharedCity.map((employee) => ({
                  label: employee.fullName,
                  value: employee.id,
                }))}
                optionLabel="label"
                optionValue="value"
                placeholder="Все метрологи"
                className="w-full"
                showClear
                disabled={!selectedMetrologistCity}
              />
            </label>
            <label className="form-control">
              <span className="label-text">Период с</span>
              <input
                type="date"
                className="input input-bordered"
                value={periodFrom}
                onChange={(e) => setPeriodFrom(e.target.value)}
              />
            </label>
            <label className="form-control">
              <span className="label-text">Период по</span>
              <input
                type="date"
                className="input input-bordered"
                value={periodTo}
                onChange={(e) => setPeriodTo(e.target.value)}
              />
            </label>
            <button
              className="btn btn-primary"
              onClick={() => void handleRefreshHouseholdWithRecalculate()}
              disabled={householdSummaryLoading || developmentLoading || !selectedMetrologistCity}
            >
              {householdSummaryLoading || developmentLoading ? 'Обновляем...' : 'Обновить остатки'}
            </button>
          </div>

          {!householdSummaryVisible ? (
            <div className="rounded-lg border border-dashed border-base-300 bg-base-100 p-4 text-sm text-base-content/70">
              Выберите город и нажмите «Обновить остатки», чтобы показать склад и остатки по метрологам.
            </div>
          ) : (
            <>
              <div className="divider">Склад</div>
              <div className="rounded-lg border border-base-300 overflow-x-auto">
                <table className="table table-sm w-full text-sm">
                  <thead>
                    <tr className="bg-base-200">
                      <th>Товар</th>
                      <th className="text-right">Приход за период</th>
                      <th className="text-right">Расход за период</th>
                      <th className="text-right">Текущий остаток</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(Object.keys(HOUSEHOLD_ITEM_LABELS) as HouseholdItemType[]).map((itemType) => (
                      <tr key={itemType}>
                        <td>{HOUSEHOLD_ITEM_LABELS[itemType]}</td>
                        <td className="text-right">{householdSummary?.periodStats.warehouseIn[itemType] ?? 0}</td>
                        <td className="text-right">{householdSummary?.periodStats.warehouseOut[itemType] ?? 0}</td>
                        <td
                          className={`text-right font-medium ${
                            (householdSummary?.warehouse[itemType] ?? 0) < 0 ? 'text-error' : 'text-success'
                          }`}
                        >
                          {householdSummary?.warehouse[itemType] ?? 0}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {householdSummary && (
                <p className="text-xs text-base-content/60">
                  Движений в базе: {householdSummary.movementsCount}. Период: {formatDateRu(periodFrom)} —{' '}
                  {formatDateRu(periodTo)}.
                </p>
              )}

              <div className="divider">Остатки по метрологам</div>

              <div className="rounded-lg border border-base-300 overflow-x-auto">
                <table className="table table-sm w-full text-sm">
                  <thead>
                    <tr className="bg-base-200">
                      <th>Сотрудник</th>
                      {(Object.keys(HOUSEHOLD_ITEM_LABELS) as HouseholdItemType[]).map((itemType) => (
                        <th className="text-right" key={`emp-bal-${itemType}`}>
                          {HOUSEHOLD_ITEM_LABELS[itemType]} (ост/расх)
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {!householdSummary || householdSummary.employees.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="text-center text-base-content/70">
                          Нет остатков по сотрудникам
                        </td>
                      </tr>
                    ) : (
                      householdSummary.employees.map((row) => (
                        <tr key={row.employeeId}>
                          <td>{row.fullName}</td>
                          {(Object.keys(HOUSEHOLD_ITEM_LABELS) as HouseholdItemType[]).map((itemType) => (
                            <td
                              className={`text-right ${row.balance[itemType] < 0 ? 'text-error font-medium' : ''}`}
                              key={`${row.employeeId}-${itemType}`}
                            >
                              <div>{row.balance[itemType]}</div>
                              <div className="text-xs text-base-content/60">
                                расх: {row.periodSpent?.[itemType] ?? 0}
                              </div>
                            </td>
                          ))}
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </>
          )}

          <div className="divider">Операции</div>

          <div className="grid grid-cols-1 md:grid-cols-8 gap-3 items-end">
            <label className="form-control">
              <span className="label-text">Город</span>
              <Dropdown
                value={householdForm.city}
                onChange={(e) => {
                  const city = e.value || '';
                  if (editingHouseholdId) {
                    setHouseholdForm((prev) => ({ ...prev, city }));
                  }
                  applySharedCity(city);
                }}
                options={developmentCityOptions}
                optionLabel="label"
                optionValue="value"
                placeholder="Выберите город"
                className="w-full"
                showClear
              />
            </label>
            <label className="form-control">
              <span className="label-text">Операция</span>
              <Dropdown
                value={householdForm.operationType}
                onChange={(e) => {
                  const operationType = (e.value || 'receipt') as HouseholdMovementOperation;
                  const next =
                    operationType === 'issue'
                      ? { scope: 'employee' as HouseholdMovementScope, direction: 'in' as HouseholdMovementDirection }
                      : operationType === 'return'
                        ? { scope: 'employee' as HouseholdMovementScope, direction: 'out' as HouseholdMovementDirection }
                        : operationType === 'consume'
                          ? { scope: 'employee' as HouseholdMovementScope, direction: 'out' as HouseholdMovementDirection }
                          : { scope: 'warehouse' as HouseholdMovementScope, direction: 'in' as HouseholdMovementDirection };
                  setHouseholdForm((prev) => ({
                    ...prev,
                    operationType,
                    ...next,
                    employeeId: next.scope === 'employee' ? prev.employeeId : '',
                  }));
                }}
                options={HOUSEHOLD_OPERATION_OPTIONS}
                optionLabel="label"
                optionValue="value"
                className="w-full"
              />
            </label>
            <label className="form-control">
              <span className="label-text">Товар</span>
              <Dropdown
                value={householdForm.itemType}
                onChange={(e) =>
                  setHouseholdForm((prev) => ({ ...prev, itemType: (e.value || 'water_counter') as HouseholdItemType }))
                }
                options={HOUSEHOLD_ITEM_OPTIONS}
                optionLabel="label"
                optionValue="value"
                className="w-full"
              />
            </label>
            <label className="form-control">
              <span className="label-text">Метролог</span>
              <Dropdown
                value={householdForm.employeeId}
                onChange={(e) => setHouseholdForm((prev) => ({ ...prev, employeeId: e.value || '' }))}
                options={metrologistsBySharedCity.map((employee) => ({
                  label: employee.fullName,
                  value: employee.id,
                }))}
                optionLabel="label"
                optionValue="value"
                placeholder={householdForm.scope === 'employee' ? 'Выберите метролога' : 'Для склада не выбирается'}
                className="w-full"
                showClear
                disabled={!householdForm.city || householdForm.scope !== 'employee'}
              />
            </label>
            <label className="form-control">
              <span className="label-text">Количество</span>
              <input
                type="number"
                min={1}
                className="input input-bordered [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                value={householdForm.quantity}
                onChange={(e) => setHouseholdForm((prev) => ({ ...prev, quantity: e.target.value }))}
                onWheel={(e) => e.currentTarget.blur()}
                placeholder="0"
              />
            </label>
            <label className="form-control">
              <span className="label-text">Deal ID</span>
              <input
                type="text"
                className="input input-bordered"
                value={householdForm.dealId}
                onChange={(e) => setHouseholdForm((prev) => ({ ...prev, dealId: e.target.value }))}
                placeholder="опционально"
              />
            </label>
            <label className="form-control">
              <span className="label-text">Дата</span>
              <input
                type="date"
                className="input input-bordered"
                value={householdForm.date}
                onChange={(e) => setHouseholdForm((prev) => ({ ...prev, date: e.target.value }))}
              />
            </label>
            <label className="form-control">
              <span className="label-text">Комментарий</span>
              <input
                type="text"
                className="input input-bordered"
                value={householdForm.comment}
                onChange={(e) => setHouseholdForm((prev) => ({ ...prev, comment: e.target.value }))}
                placeholder="Необязательно"
              />
            </label>
            <div className="flex gap-2">
              <button
                className="btn btn-primary flex-1"
                onClick={() => void handleCreateHouseholdReceipt()}
                disabled={developmentLoading}
              >
                {editingHouseholdId ? 'Сохранить' : 'Добавить операцию'}
              </button>
              {editingHouseholdId && (
                <button
                  className="btn btn-ghost"
                  onClick={() => {
                    setEditingHouseholdId(null);
                    setHouseholdForm({
                      ...initialHouseholdForm,
                      city: selectedMetrologistCity,
                      date: getTodayDate(),
                    });
                  }}
                  disabled={developmentLoading}
                >
                  Отмена
                </button>
              )}
            </div>
          </div>

          <div className="rounded-lg border border-base-300 overflow-x-auto">
            <table className="table table-sm w-full text-sm">
              <thead>
                <tr className="bg-base-200">
                  <th>Город</th>
                  <th>Операция</th>
                  <th>Товар</th>
                  <th className="text-right">Кол-во</th>
                  <th>Сотрудник</th>
                  <th>Дата</th>
                  <th>Deal</th>
                  <th>Комментарий</th>
                  <th className="text-right w-44">Действия</th>
                </tr>
              </thead>
              <tbody>
                {filteredHouseholdRows.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="text-center text-base-content/70">
                      Пока нет операций
                    </td>
                  </tr>
                ) : (
                  pagedHouseholdRows.map((row) => (
                      <tr key={row.id}>
                        <td>{row.city}</td>
                        <td>
                          <span className="font-medium">
                            {HOUSEHOLD_OPERATION_LABELS[row.operationType] || row.operationType}
                          </span>
                        </td>
                        <td>{HOUSEHOLD_ITEM_LABELS[row.itemType] || row.itemType}</td>
                        <td className="text-right">{row.quantity}</td>
                        <td>{row.employee?.fullName || '-'}</td>
                        <td>{row.date ? new Date(row.date).toLocaleDateString('ru-RU') : ''}</td>
                        <td className="max-w-44 truncate">
                          {row.dealId ? (
                            row.dealUrl ? (
                              <a
                                href={row.dealUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="link link-primary"
                              >
                                {row.dealId}
                              </a>
                            ) : (
                              row.dealId
                            )
                          ) : (
                            ''
                          )}
                        </td>
                        <td>{row.comment || ''}</td>
                        <td className="text-right whitespace-nowrap space-x-2">
                          <button
                            className="btn btn-xs btn-outline"
                            onClick={() => handleEditHouseholdReceipt(row)}
                            disabled={developmentLoading}
                          >
                            Редактировать
                          </button>
                          <button
                            className="btn btn-xs btn-error btn-outline"
                            onClick={() => void handleDeleteHouseholdReceipt(row)}
                            disabled={developmentLoading}
                          >
                            Удалить
                          </button>
                        </td>
                      </tr>
                    ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeTab === 'serviceCostsDev' && (
        <div className="space-y-6">
          <div>
            <h2 className="text-xl md:text-2xl font-bold text-base-content">Себестоимость</h2>
            <p className="text-sm md:text-base font-medium text-base-content/70 mt-2">
              Себестоимость услуг по городу и месяцу. Можно выбрать несколько городов или все сразу — значения
              применятся к каждому. Пустые поля не затирают уже сохранённые цифры. В Excel «Сводная» считается
              как Кол-во × себестоимость.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
            <label className="form-control md:col-span-2">
              <span className="label-text">Города</span>
              <div className="w-full min-w-0 [&_.p-multiselect]:w-full [&_.p-multiselect]:max-w-none">
                <MultiSelect
                  value={serviceCostForm.cities}
                  onChange={(e) => setServiceCostForm((prev) => ({ ...prev, cities: e.value || [] }))}
                  options={developmentCityOptions}
                  optionLabel="label"
                  optionValue="value"
                  placeholder="Выберите один, несколько или все города"
                  display="chip"
                  filter
                  showClear
                  selectAll
                  className="w-full"
                  disabled={developmentLoading || developmentCityOptions.length === 0}
                />
              </div>
            </label>
            <label className="form-control">
              <span className="label-text">Месяц</span>
              <input
                type="month"
                className="input input-bordered"
                value={serviceCostForm.month}
                onChange={(e) => setServiceCostForm((prev) => ({ ...prev, month: e.target.value }))}
              />
            </label>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {SERVICE_COST_FIELDS.map(({ label, key }) => (
              <label className="form-control" key={key}>
                <span className="label-text">{label}</span>
                <input
                  type="number"
                  className="input input-bordered [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                  value={serviceCostForm[key]}
                  onChange={(e) =>
                    setServiceCostForm((prev) => ({
                      ...prev,
                      [key]: e.target.value,
                    }))
                  }
                  onWheel={(e) => e.currentTarget.blur()}
                  placeholder="0"
                />
              </label>
            ))}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {SERVICE_COST_EXPENSE_FIELDS.map(({ label, key, suffix }) => (
              <label className="form-control" key={key}>
                <span className="label-text">{label}{suffix ? `, ${suffix}` : ''}</span>
                <input
                  type="number"
                  className="input input-bordered [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                  value={serviceCostForm[key]}
                  onChange={(e) =>
                    setServiceCostForm((prev) => ({
                      ...prev,
                      [key]: e.target.value,
                    }))
                  }
                  onWheel={(e) => e.currentTarget.blur()}
                  placeholder="0"
                />
              </label>
            ))}
          </div>
          <div className="flex flex-wrap gap-2">
            <button className="btn btn-primary" onClick={() => void handleUpsertServiceCost()} disabled={developmentLoading}>
              {editingServiceCostId ? 'Сохранить' : 'Сохранить себестоимость'}
            </button>
            {editingServiceCostId && (
              <button
                className="btn btn-ghost"
                onClick={() => {
                  setEditingServiceCostId(null);
                  setServiceCostForm(initialServiceCostForm);
                }}
                disabled={developmentLoading}
              >
                Отмена
              </button>
            )}
          </div>
          <div className="rounded-lg border border-base-300 overflow-x-auto">
            <table className="table table-sm w-full">
              <thead>
                <tr className="bg-base-200">
                  <th>Город</th>
                  <th>Месяц</th>
                  <th className="text-right">ВС поверка</th>
                  <th className="text-right">ВС замена</th>
                  <th className="text-right">ВС продажа</th>
                  <th className="text-right">ВС опломбировка</th>
                  <th className="text-right">ВС диагностика</th>
                  <th className="text-right">ТС поверка</th>
                  <th className="text-right">ТС монтаж/демонтаж</th>
                  <th className="text-right">ТС продажа</th>
                  <th className="text-right">ТС замена АКБ</th>
                  <th className="text-right">ГС поверка</th>
                  <th className="text-right">Налог р/сч, %</th>
                  <th className="text-right">Расход/заявку</th>
                  <th className="text-right">Диспетчер</th>
                  <th>Действия</th>
                </tr>
              </thead>
              <tbody>
                {serviceCostRows.length === 0 ? (
                  <tr>
                    <td colSpan={16} className="text-center text-base-content/70">
                      Пока нет записей
                    </td>
                  </tr>
                ) : (
                  serviceCostRows.map((row) => (
                    <tr key={row.id}>
                      <td>{row.city}</td>
                      <td>{MONTH_OPTIONS.find((m) => Number(m.value) === row.month)?.label || row.month} {row.year}</td>
                      <td className="text-right">{rub(Number(row.waterVerification || 0))}</td>
                      <td className="text-right">{rub(Number(row.waterReplacement || 0))}</td>
                      <td className="text-right">{rub(Number(row.waterSale || 0))}</td>
                      <td className="text-right">{rub(Number(row.waterSealing || 0))}</td>
                      <td className="text-right">{rub(Number(row.waterDiagnostics || 0))}</td>
                      <td className="text-right">{rub(Number(row.heatVerification || 0))}</td>
                      <td className="text-right">{rub(Number(row.heatMounting || 0))}</td>
                      <td className="text-right">{rub(Number(row.heatSale || 0))}</td>
                      <td className="text-right">{rub(Number(row.heatBatteryReplacement || 0))}</td>
                      <td className="text-right">{rub(Number(row.gasVerification || 0))}</td>
                      <td className="text-right">{Number(row.bankReceiptTaxPercent || 0)}%</td>
                      <td className="text-right">{rub(Number(row.perRequestExpense || 0))}</td>
                      <td className="text-right">{rub(Number(row.dispatcherExpense || 0))}</td>
                      <td className="whitespace-nowrap space-x-2">
                        <button
                          className="btn btn-xs btn-outline"
                          onClick={() => handleEditServiceCost(row)}
                          disabled={developmentLoading}
                        >
                          Редактировать
                        </button>
                        <button
                          className="btn btn-xs btn-error btn-outline"
                          onClick={() => void handleDeleteServiceCost(row.id)}
                          disabled={developmentLoading}
                        >
                          Удалить
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {!isSalaryUser && activeTab === 'products' && (
        <div className="space-y-6">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div>
              <h2 className="text-xl md:text-2xl font-bold text-base-content">
                Услуги
              </h2>
              <p className="text-sm md:text-base font-medium text-base-content/70 mt-2">
                Управление услугами для зарплаты
              </p>
            </div>
            <button className="btn btn-primary w-full sm:w-auto" onClick={openCreateForm}>
              Добавить услугу
            </button>
          </div>

          <div className="overflow-x-auto">
            <table className="table table-zebra w-full">
              <thead>
                <tr className="bg-base-200">
                  <th className="w-[300px] text-xs md:text-base font-bold text-base-content py-2 md:py-3 px-2 md:px-4">Название</th>
                  <th className="text-xs md:text-base font-bold text-base-content py-2 md:py-3 px-2 md:px-4">Bitrix Product ID</th>
                  <th className="text-xs md:text-base font-bold text-base-content py-2 md:py-3 px-2 md:px-4">Вариации</th>
                  <th className="text-xs md:text-base font-bold text-base-content py-2 md:py-3 px-2 md:px-4">Статус</th>
                  <th className="text-xs md:text-base font-bold text-base-content py-2 md:py-3 px-2 md:px-4">Действия</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr>
                    <td colSpan={5} className="text-center py-8">
                      <span className="loading loading-spinner loading-md text-primary"></span>
                    </td>
                  </tr>
                )}

                {!loading && services.length === 0 && (
                  <tr>
                    <td colSpan={5} className="text-center py-8 text-base-content/70">
                      Услуги еще не добавлены
                    </td>
                  </tr>
                )}

                {!loading &&
                  services.map((service) => {
                    const variants = service.variants.length > 0 ? service.variants : [null];
                    return variants.map((variant, index) => (
                      <tr key={variant ? `${service.id}-${variant.id}` : `${service.id}-no-variants`}>
                        {index === 0 && (
                          <td
                            rowSpan={variants.length}
                            className="w-[300px] max-w-[300px] align-top font-bold text-xs md:text-sm text-base-content py-2 md:py-3 px-2 md:px-4 whitespace-normal break-words"
                          >
                            {service.name}
                          </td>
                        )}
                        <td className="py-2 md:py-3 px-2 md:px-4 text-xs md:text-sm font-medium text-base-content">
                          {variant ? (variant.bitrixProductId ?? '-') : '-'}
                        </td>
                        <td className="py-2 md:py-3 px-2 md:px-4 text-xs md:text-sm font-medium text-base-content">
                          {variant
                            ? `${variant.name}: ${getLatestActualRateLabel(variant)}`
                            : '-'}
                        </td>
                        <td
                          className={`py-2 md:py-3 px-2 md:px-4 text-xs md:text-sm font-medium ${
                            variant ? (variant.isActive ? 'text-success' : 'text-error') : 'text-base-content/70'
                          }`}
                        >
                          {variant ? (variant.isActive ? 'Активна' : 'Неактивна') : '-'}
                        </td>
                        {index === 0 && (
                          <td rowSpan={variants.length} className="align-top py-2 md:py-3 px-2 md:px-4">
                            <div className="flex gap-1 md:gap-2">
                              <button
                                className="btn btn-xs md:btn-sm btn-ghost"
                                onClick={() => openEditForm(service)}
                              >
                                <PencilIcon className="h-3 w-3 md:h-4 md:w-4" />
                              </button>
                              <button
                                className="btn btn-xs md:btn-sm btn-ghost text-error"
                                onClick={() => handleArchiveService(service.id)}
                                disabled={!service.isActive}
                              >
                                <TrashIcon className="h-3 w-3 md:h-4 md:w-4" />
                              </button>
                            </div>
                          </td>
                        )}
                      </tr>
                    ));
                  })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeTab === 'employees' && (
        <div className="space-y-6">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div>
              <h2 className="text-xl md:text-2xl font-bold text-base-content">Сотрудники</h2>
              <p className="text-sm md:text-base font-medium text-base-content/70 mt-2">
                Справочник сотрудников для отчетов по зарплате
              </p>
            </div>
            <button className="btn btn-primary w-full sm:w-auto" onClick={openCreateEmployeeForm}>
              Добавить сотрудника
            </button>
          </div>

          <div className="overflow-x-auto">
            <table className="table table-zebra w-full">
              <thead>
                <tr className="bg-base-200">
                  <th className="text-xs md:text-base font-bold text-base-content py-2 md:py-3 px-2 md:px-4">ФИО</th>
                  <th className="text-xs md:text-base font-bold text-base-content py-2 md:py-3 px-2 md:px-4">Город</th>
                  <th className="text-xs md:text-base font-bold text-base-content py-2 md:py-3 px-2 md:px-4">Роль</th>
                  <th className="text-xs md:text-base font-bold text-base-content py-2 md:py-3 px-2 md:px-4">Bitrix User ID</th>
                  <th className="text-xs md:text-base font-bold text-base-content py-2 md:py-3 px-2 md:px-4">Статус</th>
                  <th className="text-xs md:text-base font-bold text-base-content py-2 md:py-3 px-2 md:px-4">Не учитывается с</th>
                  <th className="text-xs md:text-base font-bold text-base-content py-2 md:py-3 px-2 md:px-4">Действия</th>
                </tr>
              </thead>
              <tbody>
                {employeesLoading && (
                  <tr>
                    <td colSpan={7} className="text-center py-8">
                      <span className="loading loading-spinner loading-md text-primary"></span>
                    </td>
                  </tr>
                )}

                {!employeesLoading && employees.length === 0 && (
                  <tr>
                    <td colSpan={7} className="text-center py-8 text-base-content/70">
                      Сотрудники не добавлены
                    </td>
                  </tr>
                )}

                {!employeesLoading &&
                  employees.map((employee) => (
                    <tr key={employee.id}>
                      <td className="font-bold text-xs md:text-sm text-base-content py-2 md:py-3 px-2 md:px-4">
                        {employee.fullName}
                      </td>
                      <td className="font-medium text-xs md:text-sm text-base-content py-2 md:py-3 px-2 md:px-4">
                        {employee.city || '-'}
                      </td>
                      <td className="font-medium text-xs md:text-sm text-base-content py-2 md:py-3 px-2 md:px-4">
                        {employee.role === 'METROLOGIST' ? 'Метролог' : 'Диспетчер'}
                      </td>
                      <td className="font-medium text-xs md:text-sm text-base-content py-2 md:py-3 px-2 md:px-4">
                        {employee.bitrixUserId ?? '-'}
                      </td>
                      <td
                        className={`font-semibold text-xs md:text-sm py-2 md:py-3 px-2 md:px-4 ${
                          employee.isActive ? 'text-success' : 'text-error'
                        }`}
                      >
                        {employee.isActive ? 'Активен' : 'Неактивен'}
                      </td>
                      <td className="font-medium text-xs md:text-sm text-base-content py-2 md:py-3 px-2 md:px-4">
                        {formatMonthYearRu(employee.inactiveFrom)}
                      </td>
                      <td className="py-2 md:py-3 px-2 md:px-4">
                        <div className="flex gap-1 md:gap-2">
                          <button
                            className="btn btn-xs md:btn-sm btn-ghost"
                            onClick={() => openEditEmployeeForm(employee)}
                          >
                            <PencilIcon className="h-3 w-3 md:h-4 md:w-4" />
                          </button>
                          <button
                            className="btn btn-xs md:btn-sm btn-ghost text-error"
                            onClick={() => handleArchiveEmployee(employee.id)}
                            disabled={!employee.isActive}
                          >
                            <TrashIcon className="h-3 w-3 md:h-4 md:w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {isFormOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-[94vw] max-w-[1200px] max-h-[90vh] overflow-y-auto rounded-xl border border-base-300 bg-base-100 shadow-xl">
            <div className="p-6 space-y-5">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold text-base-content">
                  {editingServiceId ? 'Редактировать услугу' : 'Добавить услугу'}
                </h2>
                <button className="btn btn-sm btn-ghost" onClick={() => setIsFormOpen(false)}>
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <label className="form-control">
                  <span className="label-text">Название услуги</span>
                  <input
                    className="input input-bordered"
                    value={serviceForm.name}
                    onChange={(e) => setServiceForm((prev) => ({ ...prev, name: e.target.value }))}
                    placeholder="Например: Поверка счетчика"
                  />
                </label>

                <label className="form-control">
                  <span className="label-text">Описание</span>
                  <input
                    className="input input-bordered"
                    value={serviceForm.description}
                    onChange={(e) => setServiceForm((prev) => ({ ...prev, description: e.target.value }))}
                    placeholder="Краткое описание"
                  />
                </label>
              </div>

              <label className="label cursor-pointer justify-start gap-3">
                <input
                  type="checkbox"
                  className="checkbox checkbox-primary"
                  checked={serviceForm.isActive}
                  onChange={(e) =>
                    setServiceForm((prev) => ({
                      ...prev,
                      isActive: e.target.checked,
                      variants: prev.variants.map((variant) => ({
                        ...variant,
                        isActive: e.target.checked,
                      })),
                    }))
                  }
                />
                <span className="label-text">Услуга активна</span>
              </label>

              <div className="border border-base-300 rounded-lg p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-base-content">Вариации</h3>
                  <button className="btn btn-sm btn-outline" onClick={handleAddVariant}>
                    + Добавить вариацию
                  </button>
                </div>

                {serviceForm.variants.length === 0 && (
                  <p className="text-sm text-base-content/70">Вариации пока не добавлены</p>
                )}

                {serviceForm.variants.length > 0 && (
                  <div className="hidden md:grid md:[grid-template-columns:14%_22%_19%_18%_10%_17%] gap-2.5 pl-3 pr-4 text-xs font-semibold text-base-content/70 items-center">
                    <span>Bitrix Product ID</span>
                    <span>Название</span>
                    <span>Тип начисления</span>
                    <span>Ставка</span>
                    <span>Статус</span>
                    <span className="text-center">Действие</span>
                  </div>
                )}

                {serviceForm.variants.map((variant, index) => (
                  <div
                    key={variant.id || `variant-${index}`}
                    className="rounded-lg border border-base-300 p-3 space-y-3"
                  >
                    <div className="grid grid-cols-1 md:[grid-template-columns:14%_22%_19%_18%_10%_17%] gap-2.5 items-start">
                    <label className="form-control">
                      <span className="label-text md:hidden">Bitrix Product ID</span>
                      <input
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        className="input input-bordered"
                        value={variant.bitrixProductId}
                        onChange={(e) => handleVariantChange(index, 'bitrixProductId', e.target.value.replace(/\D/g, ''))}
                        placeholder="12345"
                      />
                    </label>

                    <label className="form-control">
                      <span className="label-text md:hidden">Название</span>
                      <input
                        className="input input-bordered"
                        value={variant.name}
                        onChange={(e) => handleVariantChange(index, 'name', e.target.value)}
                        placeholder="В квартире"
                      />
                    </label>

                    <label className="form-control">
                      <span className="label-text md:hidden">Тип начисления</span>
                      <div className="flex flex-col gap-1.5">
                        <label className="label cursor-pointer justify-start gap-2 p-0">
                          <input
                            type="radio"
                            className="radio radio-sm radio-primary"
                            checked={variant.rateType === 'fixed'}
                            onChange={() => handleRateTypeChange(index, 'fixed')}
                          />
                          <span className="label-text text-xs whitespace-nowrap">Фиксированная сумма</span>
                        </label>
                        <label className="label cursor-pointer justify-start gap-2 p-0">
                          <input
                            type="radio"
                            className="radio radio-sm radio-primary"
                            checked={variant.rateType === 'percentage'}
                            onChange={() => handleRateTypeChange(index, 'percentage')}
                          />
                          <span className="label-text text-xs whitespace-nowrap">Процент</span>
                        </label>
                      </div>
                    </label>

                    <label className="form-control">
                      <span className="label-text md:hidden">Ставка</span>
                      <div className="w-full">
                        <input
                          type="number"
                          min={variant.rateType === 'fixed' ? 0 : 0.000001}
                          max={variant.rateType === 'percentage' ? 100 : undefined}
                          step={variant.rateType === 'fixed' ? '0.01' : '0.01'}
                          className="input input-bordered w-full [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                          value={variant.rateType === 'fixed' ? variant.fixedPrice : variant.percentage}
                          onChange={(e) =>
                            handleVariantChange(
                              index,
                              variant.rateType === 'fixed' ? 'fixedPrice' : 'percentage',
                              e.target.value,
                            )
                          }
                          onWheel={(e) => e.currentTarget.blur()}
                          placeholder={variant.rateType === 'fixed' ? '1000' : '10'}
                        />
                        <span className="label-text-alt mt-1 block h-4 whitespace-nowrap text-[11px] leading-4">
                          {variant.rateType === 'fixed' ? 'Значение в рублях' : 'Значение в процентах (0-100)'}
                        </span>
                      </div>
                    </label>

                    <div className="form-control">
                      <span className="label-text mb-2 md:hidden">Статус</span>
                      <label className="label cursor-pointer justify-center gap-2 p-0">
                        <input
                          type="checkbox"
                          className="checkbox checkbox-primary"
                          checked={variant.isActive}
                          onChange={(e) => handleVariantChange(index, 'isActive', e.target.checked)}
                        />
                        <span className="label-text">Активна</span>
                      </label>
                    </div>

                    <div className="form-control">
                      <span className="label-text mb-2 md:hidden">Действие</span>
                      <div className="flex justify-center items-center">
                        <button
                          className="btn btn-xs md:btn-sm btn-outline btn-error"
                          onClick={() => handleRemoveVariant(index)}
                        >
                          Удалить
                        </button>
                      </div>
                    </div>
                    </div>

                    <div className="rounded border border-base-300 p-3">
                      <div className="flex items-center justify-between mb-2">
                        <div className="text-sm font-semibold">Месячные ставки</div>
                        <button className="btn btn-xs btn-outline" onClick={() => handleAddMonthRate(index)}>
                          + Месяц
                        </button>
                      </div>
                      {variant.monthRates.length === 0 && (
                        <div className="text-xs text-base-content/70">
                          Нет месячных ставок. Будет использоваться базовая ставка вариации.
                        </div>
                      )}
                      <div className="space-y-2">
                        {variant.monthRates.map((mr, mrIndex) => (
                          <div key={`${variant.id || index}-mr-${mrIndex}`} className="rounded border border-base-300 p-2">
                            <div className="grid grid-cols-1 md:[grid-template-columns:14%_22%_19%_18%_10%_17%] gap-2.5 items-start">
                            <label className="form-control">
                              <span className="label-text text-xs opacity-0 select-none">.</span>
                              <select
                                className="select select-bordered select-sm"
                                value={mr.month}
                                onChange={(e) => handleMonthRateChange(index, mrIndex, 'month', e.target.value)}
                              >
                                {MONTH_OPTIONS.map((opt) => (
                                  <option key={opt.value} value={opt.value}>
                                    {opt.label}
                                  </option>
                                ))}
                              </select>
                              <span className="label-text-alt mt-1 block h-4 whitespace-nowrap text-[11px] leading-4">
                                Месяц
                              </span>
                            </label>
                            <label className="form-control">
                              <span className="label-text text-xs opacity-0 select-none">.</span>
                              <input
                                type="number"
                                min={2020}
                                className="input input-bordered input-sm [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                value={mr.year}
                                onChange={(e) => handleMonthRateChange(index, mrIndex, 'year', e.target.value)}
                                onWheel={(e) => e.currentTarget.blur()}
                              />
                              <span className="label-text-alt mt-1 block h-4 whitespace-nowrap text-[11px] leading-4">
                                Год
                              </span>
                            </label>
                            <div className="form-control">
                              <span className="label-text text-xs opacity-0 select-none">.</span>
                              <div className="flex flex-col gap-1.5">
                                <label className="label cursor-pointer justify-start gap-2 p-0">
                                  <input
                                    type="radio"
                                    className="radio radio-sm radio-primary"
                                    checked={mr.rateType === 'fixed'}
                                    onChange={() => handleMonthRateChange(index, mrIndex, 'rateType', 'fixed')}
                                  />
                                  <span className="label-text text-xs whitespace-nowrap">Фиксированная сумма</span>
                                </label>
                                <label className="label cursor-pointer justify-start gap-2 p-0">
                                  <input
                                    type="radio"
                                    className="radio radio-sm radio-primary"
                                    checked={mr.rateType === 'percentage'}
                                    onChange={() => handleMonthRateChange(index, mrIndex, 'rateType', 'percentage')}
                                  />
                                  <span className="label-text text-xs whitespace-nowrap">Процент</span>
                                </label>
                              </div>
                            </div>
                            <label className="form-control">
                              <span className="label-text text-xs opacity-0 select-none">.</span>
                              <input
                                type="number"
                                min={mr.rateType === 'fixed' ? 0 : 0.000001}
                                max={mr.rateType === 'percentage' ? 100 : undefined}
                                step="0.01"
                                className="input input-bordered input-sm [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                value={mr.rateType === 'fixed' ? mr.fixedPrice : mr.percentage}
                                onChange={(e) =>
                                  handleMonthRateChange(
                                    index,
                                    mrIndex,
                                    mr.rateType === 'fixed' ? 'fixedPrice' : 'percentage',
                                    e.target.value,
                                  )
                                }
                                onWheel={(e) => e.currentTarget.blur()}
                              />
                              <span className="label-text-alt mt-1 block h-4 whitespace-nowrap text-[11px] leading-4">
                                {mr.rateType === 'fixed' ? 'Значение в рублях' : 'Значение в процентах (0-100)'}
                              </span>
                            </label>
                            <div className="form-control justify-start">
                              <span className="label-text text-xs opacity-0 select-none">.</span>
                              <label className="label cursor-pointer justify-center gap-2 p-0">
                                <input
                                  type="checkbox"
                                  className="checkbox checkbox-primary"
                                  checked={mr.isActive}
                                  onChange={(e) => handleMonthRateChange(index, mrIndex, 'isActive', e.target.checked)}
                                />
                                <span className="label-text">Активна</span>
                              </label>
                            </div>
                            <div className="form-control justify-start">
                              <span className="label-text text-xs opacity-0 select-none">.</span>
                              <div className="flex justify-center">
                                <button
                                  className="btn btn-xs md:btn-sm btn-outline btn-error"
                                  onClick={() => handleRemoveMonthRate(index, mrIndex)}
                                >
                                  Удалить
                                </button>
                              </div>
                            </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex justify-end gap-2">
                <button className="btn btn-ghost" onClick={() => setIsFormOpen(false)} disabled={saving}>
                  Отмена
                </button>
                <button className="btn btn-primary" onClick={handleSaveService} disabled={saving || !serviceForm.name.trim()}>
                  {saving ? 'Сохраняем...' : editingServiceId ? 'Сохранить изменения' : 'Добавить услугу'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {isEmployeeFormOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-3xl rounded-xl border border-base-300 bg-base-100 shadow-xl">
            <div className="p-6 space-y-5">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold text-base-content">
                  {editingEmployeeId ? 'Редактировать сотрудника' : 'Добавить сотрудника'}
                </h2>
                <button className="btn btn-sm btn-ghost" onClick={() => setIsEmployeeFormOpen(false)}>
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <label className="form-control">
                  <span className="label-text">ФИО *</span>
                  <input
                    className="input input-bordered"
                    value={employeeForm.fullName}
                    onChange={(e) => setEmployeeForm((prev) => ({ ...prev, fullName: e.target.value }))}
                    placeholder="Введите ФИО"
                  />
                </label>

                <label className="form-control">
                  <span className="label-text">Роль *</span>
                  <Dropdown
                    value={employeeForm.role}
                    onChange={(e) => setEmployeeForm((prev) => ({ ...prev, role: e.value as EmployeeRole }))}
                    options={[
                      { label: 'Метролог', value: 'METROLOGIST' },
                      { label: 'Диспетчер', value: 'DISPATCHER' },
                    ]}
                    optionLabel="label"
                    optionValue="value"
                    className="w-full"
                    placeholder="Выберите роль"
                  />
                </label>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <label className="form-control">
                  <span className="label-text">Город</span>
                  <Dropdown
                    value={employeeForm.city}
                    onChange={(e) => setEmployeeForm((prev) => ({ ...prev, city: e.value || '' }))}
                    options={CITY_OPTIONS}
                    optionLabel="label"
                    optionValue="value"
                    className="w-full"
                    placeholder="Выберите город"
                    showClear
                  />
                </label>

                <label className="form-control">
                  <span className="label-text">Bitrix User ID</span>
                  <input
                    type="number"
                    min={1}
                    className="input input-bordered [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                    value={employeeForm.bitrixUserId}
                    onChange={(e) => setEmployeeForm((prev) => ({ ...prev, bitrixUserId: e.target.value }))}
                    onWheel={(e) => e.currentTarget.blur()}
                    placeholder="Например: 12345"
                  />
                </label>
              </div>

              <label className="label cursor-pointer justify-start gap-3">
                <input
                  type="checkbox"
                  className="checkbox checkbox-primary"
                  checked={employeeForm.isActive}
                  onChange={(e) =>
                    setEmployeeForm((prev) => ({
                      ...prev,
                      isActive: e.target.checked,
                      ...(e.target.checked ? { inactiveFromMonth: '', inactiveFromYear: '' } : {}),
                    }))
                  }
                />
                <span className="label-text">Сотрудник активен</span>
              </label>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <label className="form-control">
                  <span className="label-text">Не учитывается с (месяц)</span>
                  <Dropdown
                    value={employeeForm.inactiveFromMonth}
                    onChange={(e) =>
                      setEmployeeForm((prev) => ({
                        ...prev,
                        inactiveFromMonth: e.value || '',
                        isActive: false,
                      }))
                    }
                    options={Array.from({ length: 12 }, (_, idx) => {
                      const value = String(idx + 1).padStart(2, '0');
                      return { label: value, value };
                    })}
                    optionLabel="label"
                    optionValue="value"
                    className="w-full"
                    placeholder="Выберите месяц"
                    showClear
                  />
                </label>

                <label className="form-control">
                  <span className="label-text">Не учитывается с (год)</span>
                  <input
                    type="number"
                    min={2000}
                    className="input input-bordered [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                    value={employeeForm.inactiveFromYear}
                    onChange={(e) =>
                      setEmployeeForm((prev) => ({
                        ...prev,
                        inactiveFromYear: e.target.value,
                        isActive: false,
                      }))
                    }
                    onWheel={(e) => e.currentTarget.blur()}
                    placeholder="Например: 2026"
                  />
                </label>
              </div>

              <div className="flex justify-end gap-2">
                <button className="btn btn-ghost" onClick={() => setIsEmployeeFormOpen(false)} disabled={saving}>
                  Отмена
                </button>
                <button
                  className="btn btn-primary"
                  onClick={handleSaveEmployee}
                  disabled={saving || !employeeForm.fullName.trim()}
                >
                  {saving ? 'Сохраняем...' : editingEmployeeId ? 'Сохранить изменения' : 'Добавить сотрудника'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default ReportPage;
